import { createCartModuleLoader, withDeadline } from 'builder-types';
/**
 * Product Builder entry and section orchestration.
 * State, family rules, geometry, scheduling, and renderers live in dedicated modules.
 */

import {
  createImagePipeline,
  needsHiResGalleryDisplay,
} from 'builder-image';
import { classifyItemChange } from 'builder-domain';
import { createRenderScheduler } from 'builder-render-scheduler';
import {
  createGalleryMorphController,
  lerp,
} from 'builder-gallery-morph';

import {
  optionValue,
  getActiveSizeDisplay,
  getGalleryMaxInches,
  PB_SIZE_TRANSITION_MS,
  shouldAnimateDesktopGallery,
  setBoxSizePx,
  withInstantGalleryLayout,
  releaseInstantGalleryItem,
  applySizeSelectionToItem,
  scaleItemToMount,
  getGalleryReferencePx,
  runCoordsSelfTest,
} from 'builder-geometry';
import {
  frameStyleKey,
  mountCanvasRenderer,
  mountFrameRenderer,
  preloadFrameBorderImage,
  resolveFrameImg,
} from 'builder-renderers';
import { createAdapterFromPayload } from 'builder-families';
import { formatPresentmentMoney } from 'builder-money';
import {
  createStore,
  getSidebarAvailableOptionValues,
  reportAtcGateAfterBoot,
  syncItemDraft,
} from 'builder-state';
import { parseBuilderCatalog } from 'builder-catalog';
import {
  createEditorOptionPanel as createDataDrivenEditorOptionPanel,
  formatSizeCardPriceLabel,
  getEditorOptionPresentation,
  openEditorOptionGuide,
  resolveSizeCardPrice,
} from 'builder-editor-options';
import {
  closeEditorSessionForSection,
  openEditorSession,
  preloadEditorModule as preloadSharedEditorModule,
} from 'builder-editor-session';
import { lockPageScroll, unlockPageScroll } from 'builder-scroll-lock';
import {
  forgetCarousels,
  initAllCarousels,
  scheduleRevealActiveCarouselChip,
  scrollActiveChipsIntoView,
} from 'builder-carousel';
import { applyCouponPromoToRoot } from 'builder-promo';
import { assertProxyEntryReference, resolveEntrySelectedOptions } from 'builder-entry';

/* ========== Shell UI ========== */

// Mobile breakpoint — keep in sync with product-builder.css @media (max-width: 867px)
const PB_MOBILE_MAX = 867;
const MOBILE_MQ = window.matchMedia(`(max-width: ${PB_MOBILE_MAX}px)`);

let editorOpenGeneration = 0;
let editorOpenPromise = null;
let editorOpenSection = null;
let editorLoaderDialog = null;
let editorLoaderRetry = null;

function isDebugEnabled() {
  return (
    location.search.includes('debug=1') || localStorage.getItem('builder-debug') === '1'
  );
}

function pbLog(...args) {
  if (isDebugEnabled()) console.log('[product-builder]', ...args);
}

/** Always-on warnings for real failures/degrades (verbose dumps use pbLog). */
function pbWarn(...args) {
  console.warn('[product-builder]', ...args);
  // if (isDebugEnabled()) console.warn('[product-builder]', ...args);
}

export function preloadEditorModule(options) {
  return preloadSharedEditorModule(options);
}

function ensureEditorLoaderDialog() {
  if (editorLoaderDialog?.isConnected) return editorLoaderDialog;

  const dialog = document.createElement('dialog');
  dialog.className = 'pb-editor-loader';
  dialog.setAttribute('aria-labelledby', 'pb-editor-loader-status');
  dialog.innerHTML = `
    <div class="pb-editor-loader__panel">
      <span class="pb-editor-loader__spinner" aria-hidden="true"></span>
      <p id="pb-editor-loader-status" class="pb-editor-loader__status" data-pb-editor-loader-status role="status" aria-live="polite"></p>
      <div class="pb-editor-loader__actions">
        <button type="button" class="pb-editor-loader__retry" data-pb-editor-loader-retry hidden></button>
        <button type="button" class="pb-editor-loader__close" data-pb-editor-loader-close></button>
      </div>
    </div>
  `;

  dialog.querySelector('[data-pb-editor-loader-retry]')?.addEventListener('click', () => {
    editorLoaderRetry?.();
  });
  dialog.querySelector('[data-pb-editor-loader-close]')?.addEventListener('click', () => {
    cancelPendingEditorOpen();
  });
  dialog.addEventListener('cancel', (event) => {
    event.preventDefault();
    cancelPendingEditorOpen();
  });

  document.body.appendChild(dialog);
  editorLoaderDialog = dialog;
  return dialog;
}

function showEditorLoader(strings, state, retry = null) {
  const dialog = ensureEditorLoaderDialog();
  const status = dialog.querySelector('[data-pb-editor-loader-status]');
  const retryButton = dialog.querySelector('[data-pb-editor-loader-retry]');
  const closeButton = dialog.querySelector('[data-pb-editor-loader-close]');
  const spinner = dialog.querySelector('.pb-editor-loader__spinner');
  const failed = state === 'failed';

  if (status) {
    status.textContent = failed
      ? strings.editor_load_failed ??
        'The photo editor could not load. Check your connection and try again.'
      : strings.editor_loading ?? 'Loading photo editor…';
  }
  if (retryButton instanceof HTMLButtonElement) {
    retryButton.hidden = !failed;
    retryButton.textContent = strings.retry ?? 'Try again';
  }
  if (closeButton instanceof HTMLButtonElement) {
    closeButton.textContent = strings.close ?? strings.cancel ?? 'Close';
  }
  if (spinner instanceof HTMLElement) spinner.hidden = failed;

  editorLoaderRetry = retry;
  if (!dialog.open) dialog.showModal();
  if (failed && retryButton instanceof HTMLButtonElement) retryButton.focus();
}

function hideEditorLoader() {
  editorLoaderRetry = null;
  if (editorLoaderDialog?.open) editorLoaderDialog.close();
}

function cancelPendingEditorOpen() {
  editorOpenGeneration += 1;
  editorOpenPromise = null;
  editorOpenSection = null;
  hideEditorLoader();
}

function openWithEditorModule(
  section,
  strings,
  openEditor,
  { throwOnError = false, retry = false } = {}
) {
  if (editorOpenPromise) return editorOpenPromise;

  const generation = ++editorOpenGeneration;
  editorOpenSection = section;
  showEditorLoader(strings, 'loading');

  const promise = (async () => {
    try {
      await preloadEditorModule({ retry });
      if (generation !== editorOpenGeneration) return;
      hideEditorLoader();
      await openEditor();
    } catch (error) {
      if (generation !== editorOpenGeneration) return;
      pbWarn('editor:open failed', error);
      if (throwOnError) {
        hideEditorLoader();
        throw error;
      }
      showEditorLoader(strings, 'failed', () => {
        if (generation !== editorOpenGeneration) return;
        editorOpenPromise = null;
        void openWithEditorModule(section, strings, openEditor, {
          throwOnError,
          retry: true,
        });
      });
      if (!throwOnError) globalThis.window?.reportToWorkManager?.(new Error('Builder editor open failed'), { provider: 'builder', operation: 'open', stage: 'editor', kind: 'network', classificationStatus: 'failed' });
    } finally {
      if (editorOpenPromise === promise) editorOpenPromise = null;
      if (generation === editorOpenGeneration && !editorLoaderRetry) {
        editorOpenSection = null;
      }
    }
  })();

  editorOpenPromise = promise;
  return promise;
}

function loadPayload() {
  const node = document.getElementById('pb-payload');
  if (!node) {
    throw new Error('Product builder: missing #pb-payload');
  }
  const root = JSON.parse(node.textContent);
  const catalog = parseBuilderCatalog(root.catalog ?? root);
  return {
    ...catalog,
    ...root,
    catalog,
    family: catalog.family,
    productId: catalog.productId,
    options: catalog.options,
    availableValuesByOption: catalog.availableValuesByOption,
    variantsByOptionKey: catalog.variantsByOptionKey,
    addOnsBySize: catalog.addOnsBySize,
    frameMetafields: catalog.frameMetafields,
  };
}

function assertPayload(payload) {
  if (!payload) {
    throw new Error('Product builder: missing #pb-payload');
  }
  if (!payload.family) {
    throw new Error('Product builder: payload missing family');
  }
  assertProxyEntryReference(payload);
  if (payload.catalog?.configured !== true) {
    const diagnostics = (payload.catalog?.diagnostics ?? [])
      .map((entry) => String(entry?.code ?? entry))
      .filter(Boolean)
      .join(', ');
    throw new Error(
      `Product builder: catalog transport is incomplete${diagnostics ? ` (${diagnostics})` : ''}`
    );
  }

  const sizes = payload.availableValuesByOption?.size ?? [];
  const frames = payload.availableValuesByOption?.frame ?? [];
  if (!sizes.length || !frames.length) {
    throw new Error('Product builder: product must have Size and Frame options with values');
  }

  if (!Object.keys(payload.variantsByOptionKey ?? {}).length) {
    throw new Error('Product builder: no variants in catalog');
  }
  const completeness = payload.catalog.completeness;
  const expectedVariantCount = completeness?.expectedVariantCount;
  const emittedVariantCount = Object.keys(payload.variantsByOptionKey ?? {}).length;
  if (completeness?.complete !== true
    || emittedVariantCount !== expectedVariantCount
    || emittedVariantCount !== completeness.emittedVariantCount
    || emittedVariantCount !== payload.catalog.variants?.length) {
    throw new Error(
      `Product builder: catalog variant transport does not match its authoritative count (${emittedVariantCount}/${expectedVariantCount})`
    );
  }
  resolveEntrySelectedOptions(payload);
}

function assertVariant(adapter, selectedOptions) {
  const resolved = adapter.variantResolver.resolveItem(selectedOptions);
  if (!resolved) {
    throw new Error(
      `Product builder: no variant for size="${selectedOptions.size}" frame="${selectedOptions.frame}"`
    );
  }
  return resolved;
}

const reportedBuilderFailures = new WeakSet();
const reportedBuilderFailureValues = new Set();

function reportBuilderFailure(section, error, details = {}) {
  const isObjectFailure = (typeof error === 'object' && error !== null) || typeof error === 'function';
  if (isObjectFailure) {
    if (reportedBuilderFailures.has(error)) return error;
    reportedBuilderFailures.add(error);
  } else {
    const failureValue = `${typeof error}:${String(error)}`;
    if (reportedBuilderFailureValues.has(failureValue)) return error;
    reportedBuilderFailureValues.add(failureValue);
  }

  const diagnostic = {
    stage: details.stage ?? 'unknown',
    category: details.category ?? 'programming',
    fatal: details.fatal !== false,
    sectionId: section?.dataset?.sectionId ?? null,
    family: section?.dataset?.productFamily ?? null,
    itemId: details.itemId ?? null,
  };
  console.error('[product-builder] failure', diagnostic, error);

  if (section instanceof HTMLElement && diagnostic.fatal) {
    section.dataset.pbBootFailed = 'true';
    section.dataset.pbBootStage = diagnostic.stage;
  }
  if (window.Shopify?.designMode && section instanceof HTMLElement) {
    const notice = section.querySelector('[data-pb-design-notice]');
    if (notice) {
      const message = error instanceof Error ? error.message : String(error);
      notice.textContent = `[${diagnostic.stage}/${diagnostic.category}] ${message}`;
      notice.hidden = false;
    }
  }
  const persistenceStage = /^image-(?:editor|production|source|thumbnail)-(?:read|write)$/.test(diagnostic.stage);
  if (error?.code !== 'PB_IDB_LIFECYCLE_ABORT') globalThis.window?.reportToWorkManager?.(new Error('Builder operation failed'), {
    provider: persistenceStage ? 'indexeddb' : 'builder',
    operation: persistenceStage ? (diagnostic.stage.endsWith('-write') ? 'write' : 'hydrate') : 'initialize',
    stage: diagnostic.stage,
    kind: error?.name === 'QuotaExceededError' ? 'quota' : diagnostic.category,
    classificationStatus: 'failed',
  });
  return error;
}

function logDebugState(payload, adapter, store) {
  const selected = store.getState().selectedOptions;
  const resolved = adapter.variantResolver.resolveItem(selected);
  pbLog('builder', {
    family: payload.family,
    handle: payload.productHandle,
    variants: Object.keys(payload.variantsByOptionKey ?? {}).length,
    selection: selected,
    variantId: resolved?.id ?? null,
    items: store.getState().items.length,
  });
}

function syncDialogBodyLock() {
  const hasOpen = document.querySelector('[data-pb-dialog]:not([hidden])');
  if (hasOpen) lockPageScroll('pb-dialog-open');
  else unlockPageScroll('pb-dialog-open');
}

function openDialog(dialog) {
  if (!dialog) return;
  dialog.hidden = false;
  lockPageScroll('pb-dialog-open');
  if (dialog.hasAttribute('data-pb-style-guide')) {
    prepareStyleGuide(dialog);
  }
}

function closeDialog(dialog) {
  if (!dialog) return;
  dialog.hidden = true;
  syncDialogBodyLock();
}

/**
 * Old-PDP-styled apply-to-all prompt (replaces native `window.confirm`).
 * Resolves `true` on Apply; `false` on Discard / backdrop / Esc / missing dialog.
 * @param {ParentNode} root
 * @returns {Promise<boolean>}
 */
function promptApplyToAll(root) {
  const dialog = root.querySelector('[data-pb-apply-to-all-dialog]');
  if (!(dialog instanceof HTMLElement)) return Promise.resolve(false);

  return new Promise((resolve) => {
    let settled = false;
    const applyBtn = dialog.querySelector('[data-pb-apply-to-all-apply]');

    const finish = (accepted) => {
      if (settled) return;
      settled = true;
      applyBtn?.removeEventListener('click', onApply);
      observer.disconnect();
      if (!dialog.hidden) closeDialog(dialog);
      resolve(accepted);
    };

    const onApply = (event) => {
      event.preventDefault();
      finish(true);
    };

    const observer = new MutationObserver(() => {
      if (dialog.hidden) finish(false);
    });
    observer.observe(dialog, { attributes: true, attributeFilter: ['hidden'] });

    if (applyBtn instanceof HTMLElement) {
      applyBtn.addEventListener('click', onApply);
    }

    openDialog(dialog);
    if (applyBtn instanceof HTMLElement) {
      applyBtn.focus();
    }
  });
}

/** @type {WeakMap<HTMLElement, { onScroll: Function, onThumbClick: Function, onDotClick: Function }>} */
const styleGuideBindings = new WeakMap();

function setStyleGuideActive(dialog, thumb) {
  if (!(dialog instanceof HTMLElement) || !(thumb instanceof HTMLElement)) return;

  const name = thumb.getAttribute('data-name') || '';
  const desc = thumb.getAttribute('data-desc') || '';
  const image = thumb.getAttribute('data-image') || '';

  dialog.querySelectorAll('[data-pb-sg-thumb]').forEach((btn) => {
    const active = btn === thumb;
    btn.classList.toggle('is-active', active);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
  });

  const nameEl = dialog.querySelector('[data-pb-sg-name]');
  const descEl = dialog.querySelector('[data-pb-sg-desc]');
  const heroEl = dialog.querySelector('[data-pb-sg-hero]');
  if (nameEl) nameEl.textContent = name;
  if (descEl) descEl.textContent = desc;
  if (heroEl instanceof HTMLImageElement) {
    heroEl.src = image;
    heroEl.alt = name;
  }
}

function syncStyleGuideDots(dialog, activeIndex) {
  const dots = dialog.querySelectorAll('[data-pb-sg-dots] .pb-style-guide__dot');
  dots.forEach((dot, index) => {
    dot.classList.toggle('is-active', index === activeIndex);
    dot.setAttribute('aria-selected', index === activeIndex ? 'true' : 'false');
  });
}

function getStyleGuideActiveCardIndex(track) {
  if (!(track instanceof HTMLElement)) return 0;
  const cards = [...track.querySelectorAll('[data-pb-sg-card]')];
  if (!cards.length) return 0;

  const trackLeft = track.scrollLeft;
  let bestIndex = 0;
  let bestDist = Number.POSITIVE_INFINITY;

  cards.forEach((card, index) => {
    if (!(card instanceof HTMLElement)) return;
    const dist = Math.abs(card.offsetLeft - trackLeft);
    if (dist < bestDist) {
      bestDist = dist;
      bestIndex = index;
    }
  });

  return bestIndex;
}

function ensureStyleGuideDots(dialog) {
  const dotsRoot = dialog.querySelector('[data-pb-sg-dots]');
  const track = dialog.querySelector('[data-pb-sg-track]');
  if (!(dotsRoot instanceof HTMLElement) || !(track instanceof HTMLElement)) return;

  const cards = track.querySelectorAll('[data-pb-sg-card]');
  if (dotsRoot.childElementCount === cards.length) return;

  dotsRoot.replaceChildren();
  cards.forEach((card, index) => {
    const dot = document.createElement('button');
    dot.type = 'button';
    dot.className = 'pb-style-guide__dot';
    dot.setAttribute('data-pb-sg-dot', String(index));
    const label = card.getAttribute('data-value') || `Style ${index + 1}`;
    dot.setAttribute('aria-label', label);
    dotsRoot.appendChild(dot);
  });
}

function prepareStyleGuide(dialog) {
  if (!(dialog instanceof HTMLElement)) return;

  ensureStyleGuideDots(dialog);

  if (!styleGuideBindings.has(dialog)) {
    const track = dialog.querySelector('[data-pb-sg-track]');
    const thumbsRoot = dialog.querySelector('[data-pb-sg-thumbs]');
    const dotsRoot = dialog.querySelector('[data-pb-sg-dots]');

    const onScroll = () => {
      if (!(track instanceof HTMLElement)) return;
      syncStyleGuideDots(dialog, getStyleGuideActiveCardIndex(track));
    };

    const onThumbClick = (event) => {
      const thumb = event.target.closest('[data-pb-sg-thumb]');
      if (!(thumb instanceof HTMLElement) || !thumbsRoot?.contains(thumb)) return;
      setStyleGuideActive(dialog, thumb);
    };

    const onDotClick = (event) => {
      const dot = event.target.closest('[data-pb-sg-dot]');
      if (!(dot instanceof HTMLElement) || !dotsRoot?.contains(dot)) return;
      if (!(track instanceof HTMLElement)) return;

      const index = Number(dot.getAttribute('data-pb-sg-dot'));
      const card = track.querySelectorAll('[data-pb-sg-card]')[index];
      if (!(card instanceof HTMLElement)) return;
      track.scrollTo({ left: card.offsetLeft, behavior: 'smooth' });
      syncStyleGuideDots(dialog, index);
    };

    thumbsRoot?.addEventListener('click', onThumbClick);
    dotsRoot?.addEventListener('click', onDotClick);
    track?.addEventListener('scroll', onScroll, { passive: true });
    styleGuideBindings.set(dialog, { onScroll, onThumbClick, onDotClick });
  }

  const firstThumb = dialog.querySelector('[data-pb-sg-thumb]');
  if (firstThumb instanceof HTMLElement) {
    setStyleGuideActive(dialog, firstThumb);
  }

  const track = dialog.querySelector('[data-pb-sg-track]');
  if (track instanceof HTMLElement) {
    track.scrollTo({ left: 0, behavior: 'auto' });
  }
  syncStyleGuideDots(dialog, 0);
}

function isMobileLayout() {
  return MOBILE_MQ.matches;
}

function findActiveMobileSection() {
  if (!isMobileLayout()) return null;

  return (
    document.querySelector('[data-pb-section]:has(.pb-more-info__toggle:checked)') ||
    document.querySelector('[data-pb-section]:has([data-pb-panel]:checked:not([data-pb-panel-none]))') ||
    document.querySelector('[data-pb-section]:has(.pb-summary__toggle-input:checked)')
  );
}

function initDialogTriggers() {
  document.addEventListener('click', (event) => {
    const openButton = event.target.closest('[data-pb-dialog-open]');
    if (openButton) {
      const targetId = openButton.getAttribute('data-pb-dialog-open');
      if (targetId) openDialog(document.getElementById(targetId));
      return;
    }

    const closeButton = event.target.closest('[data-pb-dialog-close]');
    if (closeButton) {
      closeDialog(closeButton.closest('[data-pb-dialog]'));
      return;
    }

    const backdrop = event.target.closest('[data-pb-dialog]');
    if (backdrop && event.target === backdrop) closeDialog(backdrop);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;

    const openDialogs = document.querySelectorAll('[data-pb-dialog]:not([hidden])');
    if (openDialogs.length) {
      closeDialog(openDialogs[openDialogs.length - 1]);
      return;
    }

    const section = findActiveMobileSection();
    if (!section) return;

    const moreInfoToggle = section.querySelector('.pb-more-info__toggle');
    if (moreInfoToggle?.checked) {
      moreInfoToggle.checked = false;
      return;
    }

    const panelNone = section.querySelector('[data-pb-panel-none]');
    if (panelNone && !panelNone.checked) {
      panelNone.checked = true;
      return;
    }

    const summaryToggle = section.querySelector('.pb-summary__toggle-input');
    if (summaryToggle?.checked) summaryToggle.checked = false;
  });
}

function updateChipAvailability(section, adapter, state) {
  section.querySelectorAll('[data-pb-option]').forEach((optionBlock) => {
    const optionId = optionBlock.getAttribute('data-pb-option');
    const optionMeta = adapter.options?.find((entry) => entry.id === optionId);
    if (!optionMeta?.affectsVariant) return;

    const validValues = getSidebarAvailableOptionValues(adapter, state, optionId);
    const validKeys = new Set(validValues.map((value) => optionValue(optionId, value)));

    optionBlock.querySelectorAll('[data-pb-chip]').forEach((chip) => {
      const rawValue = chip.getAttribute('data-value');
      const key = optionValue(optionId, rawValue);
      const isValid = validKeys.has(key);

      chip.disabled = !isValid;
      chip.setAttribute('aria-disabled', isValid ? 'false' : 'true');
    });
  });
}

function hydrateCouponPromo(section) {
  const view = section.ownerDocument?.defaultView ?? globalThis.window;
  applyCouponPromoToRoot(section, {
    storage: view?.localStorage,
    defaultCoupon: view?.defaultCoupon,
    documentRef: section.ownerDocument,
  });
}

function initShell(ctx) {
  const { section, payload, adapter, store, refresh = false } = ctx;

  if (!refresh) {
    initAllCarousels(section);
  }

  hydrateCouponPromo(section);
  updateChipAvailability(section, adapter, store.getState());

  if (isDebugEnabled()) {
    logDebugState(payload, adapter, store);
  }
}

function registerShellEvents(reloadFn) {
  initDialogTriggers();
  document.addEventListener('shopify:section:load', (event) => reloadFn(event.target));
  document.addEventListener('shopify:block:select', (event) => reloadFn(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = resolveBuilderSection(event.target);
    if (root) destroySection(root);
  });
}

/* ========== App boot ========== */

/** Product builder app — Phase 2 entry (store + gallery + renderers). */

/** @type {WeakMap<HTMLElement, ReturnType<typeof createStore>>} */
const stores = new WeakMap();
/** @type {WeakMap<HTMLElement, ReturnType<typeof createImagePipeline>>} */
const imagePipelines = new WeakMap();
/** @type {WeakMap<HTMLElement, ReturnType<typeof parseBuilderCatalog>>} */
const builderCatalogs = new WeakMap();
/** @type {WeakMap<HTMLElement, () => Promise<{ uploadsEnabled?: boolean, start?: Function }|null>>} */
const uploadManagerEnsures = new WeakMap();
/** @type {WeakMap<HTMLElement, Set<Function>>} */
const sectionCleanups = new WeakMap();
/** @type {WeakMap<HTMLElement, string>} */
const sectionShellMarkup = new WeakMap();
/** @type {WeakMap<HTMLElement, { id: number, disposed: boolean, failed: boolean, stage: string, promise: Promise<void>|null }>} */
const bootAttempts = new WeakMap();
let nextBootAttemptId = 0;

function registerSectionCleanup(section, cleanup) {
  let cleanups = sectionCleanups.get(section);
  if (!cleanups) {
    cleanups = new Set();
    sectionCleanups.set(section, cleanups);
  }
  cleanups.add(cleanup);
  return cleanup;
}

async function ensurePdpPresentationAsset(adapter, request = {}) {
  if (request.kind !== 'finishTexture') return true;
  const url = String(request.url || resolveFrameImg(adapter, request.value) || '').trim();
  if (!url) return true;
  const loaded = await preloadFrameBorderImage(url);
  if (loaded) return true;
  const error = new Error('Product builder: frame texture could not be decoded');
  error.code = 'editor-frame-texture-unavailable';
  error.url = url;
  throw error;
}

async function openEditorForItem(section, store, itemId, sourceMountEl) {
  const item = store.getState().items.find((row) => row.id === itemId);
  if (!item) return;

  const pipeline = imagePipelines.get(section);
  const i18n = readGalleryI18n(section);
  const catalog = builderCatalogs.get(section);
  if (!catalog) throw new Error('Product builder: editor catalog is unavailable');
  let galleryCtx = galleryContexts.get(section);
  if (!galleryCtx) {
    const grid = section.querySelector('[data-pb-gallery-grid]');
    if (grid) {
      galleryCtx = ensureGalleryContext(section, store, grid);
    }
  }

  await openWithEditorModule(section, i18n, async () => {
    if (!store.getState().items.some((row) => row.id === itemId)) return;
    const session = await openEditorSession({
      context: {
        section,
        store,
        adapter: store.adapter,
        getProductionBlob: (id) =>
          pipeline?.getProductionBlob(id) ?? Promise.resolve(null),
        blobUrls: store.blobUrls,
        strings: i18n,
        family: store.getState().family,
        mountPreview: (draft, el) => {
          if (!galleryCtx) return null;
          return mountItemRenderer(store, draft, el, false, galleryCtx);
        },
        ensureEditorDisplayUrl: (itemId, options) =>
          pipeline?.ensureEditorDisplayUrl(itemId, options) ?? Promise.resolve(null),
        syncItemDraft: (draft) => syncItemDraft(draft, store.adapter),
        applySizeSelection: (draft, sizeValue) =>
          applySizeSelectionToItem(draft, sizeValue, store.adapter),
        mountOptionPanel: (optionId, draft, handlers) =>
          createDataDrivenEditorOptionPanel({
            catalog,
            adapter: store.adapter,
            optionId,
            draft,
            onSelect: handlers.onSelect,
            ensurePresentationAsset: handlers.ensurePresentationAsset,
            onAssetError: handlers.onAssetError,
            assetErrorMessage: handlers.assetErrorMessage,
            getDraft: handlers.getDraft,
            documentRef: section.ownerDocument,
            strings: i18n,
          }),
        ensurePresentationAsset: (request) =>
          ensurePdpPresentationAsset(store.adapter, request),
        getOptionPresentation: (optionId) => getEditorOptionPresentation({
          catalog,
          adapter: store.adapter,
          optionId,
          strings: i18n,
        }),
        openOptionGuide: (optionId, details = {}) => openEditorOptionGuide({
          presentation: getEditorOptionPresentation({
            catalog,
            adapter: store.adapter,
            optionId,
            strings: i18n,
          }),
          strings: i18n,
          selectedValue: details.selectedValue,
          returnFocusEl: details.returnFocusEl,
          documentRef: section.ownerDocument,
          windowRef: section.ownerDocument?.defaultView,
        }),
        initCarousels: (root) => initAllCarousels(root),
        revealActiveCarouselChip: (root, options) =>
          scheduleRevealActiveCarouselChip(root, options),
        defaultCoupon: section.ownerDocument?.defaultView?.defaultCoupon
          ?? globalThis.window?.defaultCoupon
          ?? null,
        sizePromoText:
          section.querySelector('.pb-option-block--size .pb-size-selector__promo-tag-text')
            ?.textContent
            ?.trim() ?? '',
        sourceMountEl: sourceMountEl ?? null,
      },
      itemId,
    });
    void session.closed;
  });
}

/** @type {WeakMap<HTMLElement, GalleryContext>} */
const galleryContexts = new WeakMap();
/** @type {WeakMap<HTMLElement, ReturnType<typeof createRenderScheduler>>} */
const renderSchedulers = new WeakMap();
const UPLOAD_PREVIEW_ID = '__upload_preview__';

if (!customElements.get('pb-gallery')) {
  customElements.define('pb-gallery', class PbGallery extends HTMLElement {});
}

if (!customElements.get('pb-item')) {
  customElements.define('pb-item', class PbItem extends HTMLElement {});
}

export function getStoreForSection(section) {
  return stores.get(section) ?? null;
}

function readSizeDisplay(chip) {
  const w = Number(chip.getAttribute('data-pb-display-w'));
  const h = Number(chip.getAttribute('data-pb-display-h'));
  if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) {
    return { displayWidth: w, displayHeight: h };
  }
  return {};
}

function chipMatchesSizeDisplay(chip, display) {
  const w = Number(chip.getAttribute('data-pb-display-w'));
  const h = Number(chip.getAttribute('data-pb-display-h'));
  return w === display.w && h === display.h;
}

function findSizeChip(chips, value, display) {
  if (display) {
    const byDisplay = chips.find((chip) => chipMatchesSizeDisplay(chip, display));
    if (byDisplay) return byDisplay;
  }

  return chips.find((chip) => chip.getAttribute('data-value') === String(value)) ?? chips[0];
}

function formatItemSizeLabel(item) {
  const w = item.displayWidth ?? item.size?.w;
  const h = item.displayHeight ?? item.size?.h;
  if (w && h) return `${w}x${h}`;
  return item.size?.variant ?? item.selectedOptions?.size ?? '';
}

function mountItemRenderer(store, item, mountEl, lite, ctx) {
  const family = store.getState().family;
  const mode = lite ? 'lite' : 'full';
  const rendererOptions = {
    mode,
    mountEl,
    getGalleryMax: () => ctx.maxInches,
    ensureEditorDisplayUrl: (itemId) => {
      const section = mountEl.closest('[data-pb-section]');
      const pipeline = section instanceof HTMLElement ? imagePipelines.get(section) : null;
      return pipeline?.ensureEditorDisplayUrl?.(itemId) ?? Promise.resolve(null);
    },
  };
  if (family === 'canvas') {
    return mountCanvasRenderer(mountEl, item, store.adapter, rendererOptions);
  }
  return mountFrameRenderer(mountEl, item, store.adapter, rendererOptions);
}

function formatGalleryItemPriceLabel(item, adapter, acrylicLabel) {
  const base =
    item.priceCents != null && item.priceCents !== ''
      ? formatMoneyCents(item.priceCents, adapter)
      : item.priceFormatted || formatMoneyCents(0, adapter);
  const acrylicOn =
    item.selectedOptions?.acrylic === true || item.selectedOptions?.acrylic === 'true';
  if (!acrylicOn) return base;

  const addOn = adapter?.variantResolver?.resolveAddOn?.(item.selectedOptions);
  if (!addOn?.priceCents && !addOn?.priceFormatted) return base;

  const acrylicPrice = formatMoneyCents(addOn.priceCents || 0, adapter);
  return `${base} + ${acrylicPrice} ${acrylicLabel}`;
}

function patchItemTileChrome(itemEl, item, adapter, ctx, options = {}) {
  const w = item.tileWidth ?? item.displayWidth ?? item.size?.w ?? 4;
  const h = item.tileHeight ?? item.displayHeight ?? item.size?.h ?? 5;
  if (w > 0 && h > 0) {
    itemEl.style.setProperty('--pb-item-ratio', `${w} / ${h}`);
  }

  const frameOption = item.selectedOptions?.frame ?? '';
  itemEl.dataset.productFrame = frameOption ? frameStyleKey(frameOption) : 'none';

  if (ctx?.maxInches && !options.skipLayout) {
    patchItemTileLayout(itemEl, item, ctx.maxInches);
  }

  const priceEl = itemEl.querySelector('[data-pb-item-price]');
  if (priceEl) {
    priceEl.textContent = formatGalleryItemPriceLabel(
      item,
      adapter,
      ctx?.i18n?.acrylic_meta
    );
  }

  const lowResolutionEl = itemEl.querySelector('[data-pb-item-low-resolution]');
  if (lowResolutionEl) {
    const lowResolution = item.image?.quality?.lowResolution === true;
    lowResolutionEl.hidden = !lowResolution;
    if (!lowResolution) lowResolutionEl.setAttribute('aria-expanded', 'false');
  }

  const statusEl = itemEl.querySelector('[data-pb-item-status]');
  if (statusEl) {
    const image = item.image;
    const showQuota =
      image?.production?.requiresReselect || image?.production?.missingReason === 'quota_exceeded';
    // Upload failures are intentionally NOT shown per-tile — uploads auto-retry in
    // the background and surface only as the overall "Uploading photos… n/n" count.
    const showProcessing = image?.status === 'processing' || image?.status === 'pending';
    statusEl.hidden = !(showQuota || showProcessing);
    statusEl.classList.toggle('pbi-status--quota', showQuota);
    statusEl.classList.remove('pbi-status--error');
    if (showQuota) {
      statusEl.textContent = statusEl.dataset.labelQuota;
    } else if (showProcessing) {
      statusEl.textContent = statusEl.dataset.labelProcessing;
    }
  }

}

// Size each desktop tile to its rendered frame — avoids empty padding on landscape sizes.

function patchItemTileLayout(itemEl, item, galleryMaxIn) {
  const inner = itemEl.querySelector('.pbi-inner');
  if (!inner) return;

  const grid = itemEl.closest('[data-pb-gallery-grid]');
  const scaled = scaleItemToMount(item, inner, galleryMaxIn);

  if (window.matchMedia('(max-width: 867px)').matches) {
    inner.style.aspectRatio = 'unset';
    inner.style.width = `${scaled.w}px`;
    inner.style.height = `${scaled.h}px`;
    inner.style.setProperty('--pb-tile-w', `${scaled.w}px`);
    inner.style.setProperty('--pb-tile-h', `${scaled.h}px`);
    return;
  }

  setBoxSizePx(inner, scaled.w, scaled.h, shouldAnimateDesktopGallery(grid));
  inner.style.setProperty('--pb-tile-w', `${scaled.w}px`);
  inner.style.setProperty('--pb-tile-h', `${scaled.h}px`);
}

function prepareShellMorphEntry(itemEl, item, galleryMaxIn) {
  const inner = itemEl?.querySelector('.pbi-inner');
  if (!(inner instanceof HTMLElement)) return null;
  const target = scaleItemToMount(item, inner, galleryMaxIn);
  const startWidth =
    Number.parseFloat(inner.style.width) ||
    Number.parseFloat(inner.style.getPropertyValue('--pb-tile-w')) ||
    target.w;
  const startHeight =
    Number.parseFloat(inner.style.height) ||
    Number.parseFloat(inner.style.getPropertyValue('--pb-tile-h')) ||
    target.h;

  const apply = (progress) => {
    const width = lerp(startWidth, target.w, progress);
    const height = lerp(startHeight, target.h, progress);
    inner.style.aspectRatio = 'unset';
    inner.style.width = `${width}px`;
    inner.style.height = `${height}px`;
    inner.style.setProperty('--pb-tile-w', `${width}px`);
    inner.style.setProperty('--pb-tile-h', `${height}px`);
  };

  return {
    sample: apply,
    finish: () => apply(1),
    cancel: () => {},
  };
}

function buildUploadPreviewItem(store) {
  return store.buildItemFromSidebar({
    id: UPLOAD_PREVIEW_ID,
    image: null,
  });
}

function syncUploadPreview(section, store, ctx, options = {}) {
  const uploadEl = section.querySelector('[data-pb-gallery-upload]');
  const mountEl = uploadEl?.querySelector('[data-pb-item-mount]');
  if (!uploadEl || !mountEl) return;

  const previewItem = buildUploadPreviewItem(store);
  mountEl.classList.toggle('pb-upload-preview--bw', store.getState().imageDefaults?.bw === true);
  uploadEl.dataset.productFamily = store.getState().family;
  patchItemTileChrome(uploadEl, previewItem, store.adapter, ctx, {
    skipLayout: options.skipLayout === true,
  });

  const handle = ctx.mounts.get(UPLOAD_PREVIEW_ID);
  if (!handle) {
    ctx.mounts.set(
      UPLOAD_PREVIEW_ID,
      mountItemRenderer(store, previewItem, mountEl, false, ctx)
    );
  } else {
    const dirtyDomains = classifyItemChange(ctx.previewItem, previewItem);
    handle.update?.(previewItem, dirtyDomains);
  }

  ctx.previewItem = previewItem;
  return previewItem;
}

function settleGalleryMorph(ctx, grid) {
  if (ctx.morphController?.active) {
    ctx.morphController.cancel({ finish: true });
  } else {
    commitDeferredMorphEntries(ctx);
  }
  grid?.classList.remove('pb-gallery--animating', 'pb-gallery--controlled-morph');
}

function schedulePendingGalleryRefit(section, store, ctx) {
  if (!ctx.pendingResizeRefit || ctx.pendingResizeRefitFrame) return;
  ctx.pendingResizeRefitFrame = requestAnimationFrame(() => {
    ctx.pendingResizeRefitFrame = 0;
    if (ctx.morphController?.active) return;
    if (!ctx.pendingResizeRefit) return;
    ctx.pendingResizeRefit = false;
    refitGalleryLayouts(section, store, ctx, { instant: true });
  });
}

function refitGalleryLayouts(section, store, ctx, options = {}) {
  const grid = ctx.gridEl ?? section.querySelector('[data-pb-gallery-grid]');
  if (!grid) return;
  ctx.pendingResizeRefit = false;
  settleGalleryMorph(ctx, grid);

  const items = store.getState().items;
  ctx.sectionEl = section;
  ctx.store = store;

  const apply = () => {
    ctx.gridEl = grid;
    ctx.maxInches = getGalleryMaxInches(items, store);

    if (window.matchMedia('(max-width: 867px)').matches) {
      const ref = getGalleryReferencePx(ctx.maxInches, grid);
      grid.style.setProperty('--pb-gallery-row-h', `${ref.h}px`);
    } else {
      grid.style.removeProperty('--pb-gallery-row-h');
    }

    const uploadTile = grid.querySelector('[data-pb-gallery-upload]');
    const previewItem = syncUploadPreview(section, store, ctx, { skipLayout: true });

    // Two-pass exact refit for boot/breakpoint/structure: all shell targets,
    // then all renderer targets. Animated geometry uses runGalleryGeometryMorph.
    if (uploadTile && previewItem) {
      patchItemTileLayout(uploadTile, previewItem, ctx.maxInches);
    }
    for (const item of items) {
      const itemEl =
        ctx.itemEls.get(item.id) ??
        grid.querySelector(`[data-pb-item-id="${item.id}"]`);
      if (itemEl) patchItemTileLayout(itemEl, item, ctx.maxInches);
    }
    ctx.mounts.get(UPLOAD_PREVIEW_ID)?.relayout?.({
      useTargetGeometry: true,
    });
    for (const item of items) {
      ctx.mounts.get(item.id)?.relayout?.({
        useTargetGeometry: true,
      });
    }

    const gallery = section.querySelector('[data-pb-gallery]');
    if (gallery?.classList.contains('pb-gallery--single') || items.length <= 1) {
      grid.scrollLeft = 0;
    }
  };

  withInstantGalleryLayout(grid, apply);
}

function syncActiveTile(section, selectedId, previousId = null) {
  const previous =
    previousId != null
      ? section.querySelector(`[data-pb-item-id="${previousId}"]`)
      : section.querySelector('.pb-item--active');
  if (previous && previous.getAttribute('data-pb-item-id') !== selectedId) {
    previous.classList.remove('pb-item--active');
  }
  if (!selectedId) return;
  section
    .querySelector(`[data-pb-item-id="${selectedId}"]`)
    ?.classList.add('pb-item--active');
}

function selectGalleryItem(tileEl, store, ctx, section) {
  const id = tileEl.getAttribute('data-pb-item-id');
  if (!id) return;
  const previousId = ctx.selectedId;
  ctx.selectedId = id;
  store.setSelectedItemId(id);
  syncActiveTile(section, ctx.selectedId, previousId);
  refreshRendererTiers(section, store, ctx);
}

function openGalleryEditor(tileEl, store, ctx, section) {
  if (editorOpenPromise) return;
  const id = tileEl.getAttribute('data-pb-item-id');
  if (!id) return;
  selectGalleryItem(tileEl, store, ctx, section);
  const sourceMountEl = tileEl.querySelector('[data-pb-item-mount]');
  openEditorForItem(section, store, id, sourceMountEl ?? undefined);
}

function isGalleryEditorIntent(target) {
  const tileEl = target.closest('[data-pb-tile-kind="item"]');
  if (!(tileEl instanceof HTMLElement)) return false;
  const action = target.closest('[data-pb-tile-action]')?.getAttribute('data-pb-tile-action');
  return action === 'edit' || action === 'crop' || Boolean(target.closest('[data-pb-item-mount]'));
}

function handleGalleryPointerdown(event) {
  if (event.button !== 0) return;
  const target = event.target;
  if (!(target instanceof Element) || !isGalleryEditorIntent(target)) return;
  void preloadEditorModule().catch((error) => {
    pbWarn('editor:intent preload failed', error);
  });
}

function handleGalleryClick(event, section, store, ctx) {
  const target = event.target;
  if (!(target instanceof Element)) return;

  if (target.closest('[data-pb-upload-trigger], [data-pb-tile-kind="upload"]')) {
    section.querySelector('[data-pb-file-input]')?.click();
    return;
  }

  const tileEl = target.closest('[data-pb-tile-kind="item"]');
  if (!(tileEl instanceof HTMLElement)) return;

  const action = target.closest('[data-pb-tile-action]')?.getAttribute('data-pb-tile-action');
  if (action) {
    event.stopPropagation();
    const itemId = tileEl.getAttribute('data-pb-item-id');
    if (!itemId) return;

    if (action === 'remove') {
      store.removeItem(itemId);
      return;
    }
    if (action === 'low-resolution') {
      const lowResolutionEl = target.closest('[data-pb-item-low-resolution]');
      const usesHover = window.matchMedia?.('(hover: hover) and (pointer: fine)').matches;
      if (lowResolutionEl instanceof HTMLButtonElement && !usesHover) {
        const expanded = lowResolutionEl.getAttribute('aria-expanded') === 'true';
        section.querySelectorAll('[data-pb-item-low-resolution][aria-expanded="true"]').forEach((el) => {
          if (el !== lowResolutionEl) el.setAttribute('aria-expanded', 'false');
        });
        lowResolutionEl.setAttribute('aria-expanded', expanded ? 'false' : 'true');
      }
      return;
    }
    if (action === 'edit' || action === 'crop') {
      openGalleryEditor(tileEl, store, ctx, section);
    }
    return;
  }

  if (target.closest('[data-pb-item-mount]')) {
    openGalleryEditor(tileEl, store, ctx, section);
    return;
  }

  selectGalleryItem(tileEl, store, ctx, section);
}

function handleGalleryKeydown(event, section, store, ctx) {
  if (event.key !== 'Enter' && event.key !== ' ') return;
  const target = event.target;
  if (!(target instanceof HTMLElement)) return;
  if (!target.matches('[data-pb-tile-kind="item"]')) return;

  event.preventDefault();
  openGalleryEditor(target, store, ctx, section);
}

function commitDeferredMorphEntries(ctx) {
  for (const entry of ctx.deferredMorphEntries.values()) {
    entry?.commitOffscreen?.();
  }
  ctx.deferredMorphEntries.clear();
}

function runGalleryGeometryMorph(section, store, ctx, changeSet, savedScroll) {
  const grid = ctx.gridEl ?? section.querySelector('[data-pb-gallery-grid]');
  if (!(grid instanceof HTMLElement)) return false;

  const state = store.getState();
  const previousMax = ctx.maxInches;
  const nextMax = getGalleryMaxInches(state.items, store);
  const galleryMaxChanged =
    nextMax.maxW !== previousMax.maxW || nextMax.maxH !== previousMax.maxH;
  const geometryIds = new Set();
  for (const [itemId, dirtyDomains] of changeSet.items) {
    if (dirtyDomains.has('geometry')) geometryIds.add(itemId);
  }
  if (
    !geometryIds.size &&
    !galleryMaxChanged &&
    !changeSet.global.has('galleryGeometry')
  ) {
    return false;
  }

  commitDeferredMorphEntries(ctx);
  ctx.morphController.cancel();
  let anchor = null;
  if (MOBILE_MQ.matches) {
    const mid = grid.getBoundingClientRect().left + grid.clientWidth / 2;
    let best = Infinity;
    for (const tile of grid.querySelectorAll(':scope > [data-pb-item]')) {
      const r = tile.getBoundingClientRect();
      const d = Math.abs(r.left + r.width / 2 - mid);
      if (d < best) {
        best = d;
        anchor = tile;
      }
    }
  }
  ctx.maxInches = nextMax;
  const pendingVisibility = ctx.observer?.takeRecords?.() ?? [];
  for (const entry of pendingVisibility) {
    const itemId = entry.target?.getAttribute?.('data-pb-item-id');
    if (!itemId) continue;
    if (entry.isIntersecting) ctx.visibleIds.add(itemId);
    else ctx.visibleIds.delete(itemId);
    queuePendingRendererTier(
      ctx,
      itemId,
      itemId !== ctx.selectedId && !entry.isIntersecting ? 'lite' : 'full'
    );
  }
  if (!ctx.pendingTierById) ctx.pendingTierById = new Map();

  let mobileRowHeight = null;
  if (MOBILE_MQ.matches) {
    const ref = getGalleryReferencePx(ctx.maxInches, grid);
    mobileRowHeight = `${ref.h}px`;
  } else {
    grid.style.removeProperty('--pb-gallery-row-h');
  }

  const itemById = new Map(state.items.map((item) => [item.id, item]));
  const affectedIds = galleryMaxChanged
    ? new Set(itemById.keys())
    : geometryIds;
  const animatedEntries = [];
  const endpointCommits = [];
  let animatedTileCount = 0;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const prepareItem = (itemId) => {
    const item = itemById.get(itemId);
    const itemEl =
      ctx.itemEls.get(itemId) ??
      grid.querySelector(`[data-pb-item-id="${itemId}"]`);
    if (!item || !(itemEl instanceof HTMLElement)) return;
    ctx.itemEls.set(itemId, itemEl);
    patchItemTileChrome(itemEl, item, store.adapter, ctx, { skipLayout: true });

    const mountEl = itemEl.querySelector('[data-pb-item-mount]');
    if (!(mountEl instanceof HTMLElement)) return;
    let handle = ctx.mounts.get(itemId);
    if (!handle) {
      const lite =
        itemId !== ctx.selectedId &&
        !ctx.visibleIds.has(itemId) &&
        itemEl.dataset.pbRendererTier !== 'full';
      handle = mountItemRenderer(store, item, mountEl, lite, ctx);
      ctx.mounts.set(itemId, handle);
    }

    const shellEntry = prepareShellMorphEntry(itemEl, item, ctx.maxInches);
    const rendererEntry = handle.prepareMorph?.(
      item,
      changeSet.items.get(itemId) ?? new Set(['geometry', 'photo'])
    );
    const shouldAnimate =
      !reducedMotion &&
      (itemId === ctx.selectedId ||
        ctx.visibleIds.has(itemId) ||
        itemEl.dataset.pbRendererTier === 'full');

    if (shouldAnimate) {
      animatedTileCount += 1;
      if (shellEntry) animatedEntries.push(shellEntry);
      if (rendererEntry) animatedEntries.push(rendererEntry);
      return;
    }

    endpointCommits.push(() => {
      shellEntry?.sample?.(1);
      shellEntry?.finish?.();
      if (reducedMotion) {
        rendererEntry?.sample?.(1);
        rendererEntry?.finish?.();
      } else if (rendererEntry) {
        ctx.deferredMorphEntries.set(itemId, rendererEntry);
      }
    });
  };

  for (const itemId of affectedIds) prepareItem(itemId);

  const uploadEl = grid.querySelector('[data-pb-gallery-upload]');
  const uploadMount = uploadEl?.querySelector('[data-pb-item-mount]');
  if (uploadEl instanceof HTMLElement && uploadMount instanceof HTMLElement) {
    const previewItem = buildUploadPreviewItem(store);
    patchItemTileChrome(uploadEl, previewItem, store.adapter, ctx, {
      skipLayout: true,
    });
    let uploadHandle = ctx.mounts.get(UPLOAD_PREVIEW_ID);
    if (!uploadHandle) {
      uploadHandle = mountItemRenderer(store, previewItem, uploadMount, false, ctx);
      ctx.mounts.set(UPLOAD_PREVIEW_ID, uploadHandle);
    }
    const shellEntry = prepareShellMorphEntry(uploadEl, previewItem, ctx.maxInches);
    const rendererEntry = uploadHandle.prepareMorph?.(
      previewItem,
      new Set(['geometry', 'photo'])
    );
    const animateUpload = !reducedMotion && (!MOBILE_MQ.matches || state.items.length === 0);
    if (animateUpload) {
      if (shellEntry) animatedEntries.push(shellEntry);
      if (rendererEntry) animatedEntries.push(rendererEntry);
    } else {
      endpointCommits.push(() => {
        shellEntry?.sample?.(1);
        shellEntry?.finish?.();
        if (reducedMotion) {
          rendererEntry?.sample?.(1);
          rendererEntry?.finish?.();
        } else if (rendererEntry) {
          ctx.deferredMorphEntries.set(UPLOAD_PREVIEW_ID, rendererEntry);
        }
      });
    }
    ctx.previewItem = previewItem;
  }

  for (const commit of endpointCommits) commit();
  const pin = () => {
    if (!anchor?.isConnected) return;
    const g = grid.getBoundingClientRect();
    const t = anchor.getBoundingClientRect();
    grid.scrollLeft += t.left + t.width / 2 - (g.left + grid.clientWidth / 2);
  };
  if (anchor) animatedEntries.push({ sample: pin, finish: pin, cancel() {} });

  const restoreScroll = () => {
    if (grid.scrollTop !== savedScroll.top) grid.scrollTop = savedScroll.top;
    if (anchor) pin();
    else if (grid.scrollLeft !== savedScroll.left) grid.scrollLeft = savedScroll.left;
  };

  grid.dataset.pbMorphAffectedCount = String(affectedIds.size);
  grid.dataset.pbMorphAnimatedTiles = String(animatedTileCount);
  grid.dataset.pbMorphAnimatedEntries = String(animatedEntries.length);
  ctx.morphController.start({
    entries: animatedEntries,
    reducedMotion,
    onStart: () => {
      grid.classList.add('pb-gallery--animating', 'pb-gallery--controlled-morph');
      if (mobileRowHeight) {
        grid.style.setProperty('--pb-gallery-row-h', mobileRowHeight);
      }
      restoreScroll();
    },
    onFinish: () => {
      grid.classList.remove('pb-gallery--animating', 'pb-gallery--controlled-morph');
      restoreScroll();
      commitDeferredMorphEntries(ctx);
      flushPendingRendererTiers(section, store, ctx);
      schedulePendingGalleryRefit(section, store, ctx);
    },
    onCancel: () => {
      grid.classList.remove('pb-gallery--animating', 'pb-gallery--controlled-morph');
      commitDeferredMorphEntries(ctx);
      schedulePendingGalleryRefit(section, store, ctx);
    },
  });

  return true;
}

function queuePendingRendererTier(ctx, itemId, tier) {
  if (!itemId) return;
  if (!ctx.pendingTierById) ctx.pendingTierById = new Map();
  ctx.pendingTierById.set(itemId, tier);
}

function flushPendingRendererTiers(section, store, ctx) {
  const pending = ctx.pendingTierById;
  if (!pending?.size) return;
  ctx.pendingTierById = new Map();
  const itemById = new Map(store.getState().items.map((item) => [item.id, item]));

  for (const [itemId, tier] of pending) {
    const itemEl =
      ctx.itemEls.get(itemId) ??
      section.querySelector(`[data-pb-item-id="${itemId}"]`);
    const item = itemById.get(itemId);
    const mountEl = itemEl?.querySelector('[data-pb-item-mount]');
    if (!item || !mountEl) continue;

    itemEl.dataset.pbRendererTier = tier;
    const handle = ctx.mounts.get(itemId);
    if (handle?.setMode) {
      handle.setMode(tier);
      continue;
    }
    if (handle) handle.destroy();
    ctx.mounts.set(itemId, mountItemRenderer(store, item, mountEl, tier === 'lite', ctx));
  }
}

function refreshRendererTiers(section, store, ctx) {
  const grid = ctx.gridEl ?? section.querySelector('[data-pb-gallery-grid]');
  if (!grid) return;

  const usesObserverState = Boolean(ctx.observer);
  const gridRect = usesObserverState ? null : grid.getBoundingClientRect();
  const itemById = new Map(store.getState().items.map((item) => [item.id, item]));

  grid.querySelectorAll('[data-pb-item]').forEach((itemEl) => {
    const itemId = itemEl.getAttribute('data-pb-item-id');
    if (!itemId) return;

    const item = itemById.get(itemId);
    const mountEl = itemEl.querySelector('[data-pb-item-mount]');
    if (!item || !mountEl) return;

    const forceFull = itemId === ctx.selectedId;
    const rect = usesObserverState ? null : itemEl.getBoundingClientRect();
    const inView = usesObserverState
      ? ctx.visibleIds.has(itemId)
      : rect.bottom > gridRect.top &&
        rect.top < gridRect.bottom &&
        rect.right > gridRect.left &&
        rect.left < gridRect.right;
    const lite = !forceFull && !inView;
    const tier = lite ? 'lite' : 'full';

    if (itemEl.dataset.pbRendererTier === tier) return;

    itemEl.dataset.pbRendererTier = tier;
    const handle = ctx.mounts.get(itemId);
    if (handle?.setMode) {
      handle.setMode(tier);
      return;
    }

    if (handle) handle.destroy();
    ctx.mounts.set(itemId, mountItemRenderer(store, item, mountEl, lite, ctx));
  });
}

function ensureGalleryContext(section, store, grid) {
  let ctx = galleryContexts.get(section);
  if (ctx) return ctx;

  ctx = {
    mounts: new Map(),
    itemEls: new Map(),
    observer: null,
    visibleIds: new Set(),
    selectedId: null,
    maxInches: { maxW: 10, maxH: 10 },
    gridEl: grid,
    gridResizeObserver: null,
    layoutReady: false,
    pendingTierById: new Map(),
    deferredMorphEntries: new Map(),
    pendingResizeRefit: false,
    pendingResizeRefitFrame: 0,
    morphController: createGalleryMorphController({
      duration: PB_SIZE_TRANSITION_MS,
    }),
    previewItem: null,
    i18n: readGalleryI18n(section),
    breakpointMedia: null,
    breakpointListener: null,
    galleryEl: null,
    galleryPointerdownListener: null,
    galleryClickListener: null,
    galleryKeydownListener: null,
  };
  if ('IntersectionObserver' in window) {
    ctx.observer = new IntersectionObserver(
      (entries) => {
        // A promoted deferred renderer joins the active clock; other tier
        // changes queue until the transaction settles.
        const animating = grid.classList.contains('pb-gallery--animating');

        for (const entry of entries) {
          const itemEl = entry.target;
          const itemId = itemEl.getAttribute('data-pb-item-id');
          if (!itemId) continue;

          if (entry.isIntersecting) ctx.visibleIds.add(itemId);
          else ctx.visibleIds.delete(itemId);
          const lite = itemId !== ctx.selectedId && !entry.isIntersecting;
          const tier = lite ? 'lite' : 'full';
          if (animating) {
            if (!lite) {
              const deferredEntry = ctx.deferredMorphEntries.get(itemId);
              if (deferredEntry) {
                ctx.deferredMorphEntries.delete(itemId);
                itemEl.dataset.pbRendererTier = 'full';
                ctx.mounts.get(itemId)?.setMode?.('full');
                ctx.morphController.attach(deferredEntry);
                continue;
              }
            }
            queuePendingRendererTier(ctx, itemId, tier);
            continue;
          }

          const item = store.getState().items.find((row) => row.id === itemId);
          const mountEl = itemEl.querySelector('[data-pb-item-mount]');
          if (!item || !mountEl) continue;

          itemEl.dataset.pbRendererTier = tier;
          let handle = ctx.mounts.get(itemId);

          if (handle?.setMode) {
            handle.setMode(tier);
            continue;
          }

          if (handle) handle.destroy();

          handle = mountItemRenderer(store, item, mountEl, lite, ctx);
          ctx.mounts.set(itemId, handle);
        }
      },
      { root: grid, rootMargin: '50px', threshold: 0.01 }
    );
  }

  const galleryEl = section.querySelector('[data-pb-gallery]');
  if (galleryEl) {
    ctx.galleryEl = galleryEl;
    ctx.galleryPointerdownListener = (event) => handleGalleryPointerdown(event);
    ctx.galleryClickListener = (event) => handleGalleryClick(event, section, store, ctx);
    ctx.galleryKeydownListener = (event) =>
      handleGalleryKeydown(event, section, store, ctx);
    galleryEl.addEventListener('pointerdown', ctx.galleryPointerdownListener, { passive: true });
    galleryEl.addEventListener('click', ctx.galleryClickListener);
    galleryEl.addEventListener('keydown', ctx.galleryKeydownListener);
  }

  galleryContexts.set(section, ctx);

  if (!ctx.gridResizeObserver && typeof ResizeObserver !== 'undefined') {
    ctx.gridResizeObserver = new ResizeObserver(() => {
      if (grid.classList.contains('pb-gallery--animating')) {
        ctx.pendingResizeRefit = true;
        return;
      }
      refitGalleryLayouts(section, store, ctx, { instant: true });
    });
    ctx.gridResizeObserver.observe(grid);
  }

  const refitOnBreakpoint = () => {
    refitGalleryLayouts(section, store, ctx, { instant: true });
  };
  ctx.breakpointMedia = window.matchMedia('(max-width: 867px)');
  ctx.breakpointListener = refitOnBreakpoint;
  ctx.breakpointMedia.addEventListener('change', refitOnBreakpoint);
  registerSectionCleanup(section, () => destroyGalleryContext(section));

  return ctx;
}

function destroyGalleryContext(section) {
  const ctx = galleryContexts.get(section);
  if (!ctx) return;
  ctx.observer?.disconnect();
  ctx.gridResizeObserver?.disconnect();
  if (ctx.pendingResizeRefitFrame) {
    cancelAnimationFrame(ctx.pendingResizeRefitFrame);
    ctx.pendingResizeRefitFrame = 0;
  }
  ctx.pendingResizeRefit = false;
  if (ctx.breakpointMedia && ctx.breakpointListener) {
    ctx.breakpointMedia.removeEventListener('change', ctx.breakpointListener);
  }
  if (ctx.galleryEl && ctx.galleryPointerdownListener) {
    ctx.galleryEl.removeEventListener('pointerdown', ctx.galleryPointerdownListener);
  }
  if (ctx.galleryEl && ctx.galleryClickListener) {
    ctx.galleryEl.removeEventListener('click', ctx.galleryClickListener);
  }
  if (ctx.galleryEl && ctx.galleryKeydownListener) {
    ctx.galleryEl.removeEventListener('keydown', ctx.galleryKeydownListener);
  }
  commitDeferredMorphEntries(ctx);
  ctx.morphController?.cancel();
  ctx.morphController?.destroy();
  for (const mount of ctx.mounts.values()) mount.destroy?.();
  ctx.mounts.clear();
  ctx.itemEls.clear();
  ctx.visibleIds.clear();
  galleryContexts.delete(section);
}

function createGalleryTile(store, item, ctx, section) {
  const template = section.querySelector('[data-pb-gallery-item-template]');
  if (!(template instanceof HTMLTemplateElement)) {
    throw new Error('Product builder: gallery item template is missing');
  }

  const itemEl = template.content.firstElementChild?.cloneNode(true);
  if (!(itemEl instanceof HTMLElement) || itemEl.tagName !== 'PB-ITEM') {
    throw new Error('Product builder: gallery item template must contain one pb-item');
  }

  itemEl.setAttribute('data-pb-item-id', item.id);
  itemEl.setAttribute('data-product-family', store.getState().family);
  ctx.itemEls.set(item.id, itemEl);
  const mountEl = itemEl.querySelector('[data-pb-item-mount]');
  if (!(mountEl instanceof HTMLElement)) {
    throw new Error('Product builder: gallery item template mount is missing');
  }

  patchItemTileChrome(itemEl, item, store.adapter, ctx);

  if (ctx.observer) {
    ctx.observer.observe(itemEl);
  } else {
    ctx.mounts.set(item.id, mountItemRenderer(store, item, mountEl, false, ctx));
  }

  releaseInstantGalleryItem(itemEl);

  return itemEl;
}

function updateGalleryTile(itemEl, item, store, ctx, dirtyDomains = null) {
  const explicitDomains = dirtyDomains instanceof Set;
  const chromeDirty =
    !explicitDomains ||
    ['style', 'geometry', 'quality', 'status', 'price'].some((domain) =>
      dirtyDomains.has(domain)
    );
  if (chromeDirty) {
    patchItemTileChrome(itemEl, item, store.adapter, ctx, { skipLayout: true });
  }
  if (explicitDomains && dirtyDomains.has('geometry') && ctx?.maxInches) {
    patchItemTileLayout(itemEl, item, ctx.maxInches);
  }

  const rendererDirty =
    !explicitDomains ||
    dirtyDomains.has('style') ||
    dirtyDomains.has('geometry') ||
    dirtyDomains.has('photo');
  if (!rendererDirty) return;

  const handle = ctx.mounts.get(item.id);
  if (handle?.update) {
    handle.update(item, dirtyDomains);
    return;
  }

  if (!handle) {
    const mountEl = itemEl.querySelector('[data-pb-item-mount]');
    if (!mountEl) return;
    ctx.mounts.set(item.id, mountItemRenderer(store, item, mountEl, true, ctx));
  }
}

function syncGalleryUploadUi(section, store) {
  const gallery = section.querySelector('[data-pb-gallery]');
  if (!gallery) return;
  const itemCount = store.getState().items.length;
  const hasItems = itemCount > 0;
  gallery.classList.toggle('pb-gallery--has-items', hasItems);
  gallery.classList.toggle('pb-gallery--empty', !hasItems);
  // One tile + carousel side-pads overflow the viewport; lock scroll so mobile
  // never shows a scrollbar or rubber-bands on size morph / finger swipe.
  gallery.classList.toggle('pb-gallery--single', itemCount === 1);
  const addBtn = gallery.querySelector('[data-pb-gallery-add-btn]');
  if (addBtn) {
    addBtn.hidden = !hasItems;
  }
  if (itemCount <= 1) {
    const grid = gallery.querySelector('[data-pb-gallery-grid]');
    if (grid) grid.scrollLeft = 0;
  }
}

function syncGallery(section, store, changeSet = null) {
  const grid = section.querySelector('[data-pb-gallery-grid]');
  if (!grid) return;

  // Preserve gallery scroll across item patches (e.g. Filestack URL swap).
  // Without this, even a single DOM move can reset scrollTop on desktop.
  const savedScrollTop = grid.scrollTop;
  const savedScrollLeft = grid.scrollLeft;

  syncGalleryUploadUi(section, store);

  const state = store.getState();
  const ctx = ensureGalleryContext(section, store, grid);
  const hasStructuralChanges =
    !changeSet ||
    changeSet.structural.added.size > 0 ||
    changeSet.structural.removed.size > 0 ||
    changeSet.structural.orderChanged;
  if (hasStructuralChanges && ctx.layoutReady) {
    settleGalleryMorph(ctx, grid);
  }

  if (!hasStructuralChanges && ctx.layoutReady) {
    const hasGeometryChange =
      changeSet.global.has('galleryGeometry') ||
      [...changeSet.items.values()].some((domains) => domains.has('geometry'));

    for (const [itemId, dirtyDomains] of changeSet.items) {
      const item = state.items.find((row) => row.id === itemId);
      const itemEl =
        ctx.itemEls.get(itemId) ??
        grid.querySelector(`[data-pb-item-id="${itemId}"]`);
      if (!item || !itemEl) continue;
      ctx.itemEls.set(itemId, itemEl);
      if (dirtyDomains.has('geometry')) {
        patchItemTileChrome(itemEl, item, store.adapter, ctx, { skipLayout: true });
        continue;
      }
      updateGalleryTile(itemEl, item, store, ctx, dirtyDomains);
    }

    if (changeSet.global.has('selection') && !hasGeometryChange) {
      syncUploadPreview(section, store, ctx);
    }

    const previousSelectedId = ctx.selectedId;
    ctx.selectedId = state.ui?.selectedItemId ?? null;
    syncActiveTile(section, ctx.selectedId, previousSelectedId);

    if (
      hasGeometryChange &&
      runGalleryGeometryMorph(section, store, ctx, changeSet, {
        top: savedScrollTop,
        left: savedScrollLeft,
      })
    ) {
      return;
    }
    return;
  }

  ctx.maxInches = getGalleryMaxInches(state.items, store);

  /** @type {Map<string, HTMLElement>} */
  const existing = new Map();
  grid.querySelectorAll('[data-pb-item]').forEach((node) => {
    const id = node.getAttribute('data-pb-item-id');
    if (id) {
      existing.set(id, node);
      ctx.itemEls.set(id, node);
    }
  });

  const nextIds = new Set(state.items.map((row) => row.id));

  for (const [id, node] of existing) {
    if (nextIds.has(id)) continue;
    ctx.observer?.unobserve(node);
    ctx.mounts.get(id)?.destroy();
    ctx.mounts.delete(id);
    ctx.itemEls.delete(id);
    ctx.visibleIds.delete(id);
    node.remove();
    if (ctx.selectedId === id) ctx.selectedId = null;
  }

  const uploadEl = grid.querySelector('[data-pb-gallery-upload]');

  // Place tiles in state.items order, moving a node only when it is actually
  // out of sequence. The previous check (`nextSibling !== uploadEl`) reordered
  // every non-last tile on every sync — that reset gallery scrollTop when
  // uploads finished and img src switched to the Filestack handle URL.
  /** @type {HTMLElement | null} */
  let prevEl = null;

  for (const item of state.items) {
    let itemEl = existing.get(item.id);

    if (!itemEl) {
      itemEl = createGalleryTile(store, item, ctx, section);
    } else {
      updateGalleryTile(itemEl, item, store, ctx);
    }

    const inPlace = prevEl
      ? itemEl.previousElementSibling === prevEl
      : itemEl.isConnected && itemEl === grid.querySelector('[data-pb-item]');

    if (!inPlace) {
      if (prevEl) {
        prevEl.after(itemEl);
      } else {
        const first = grid.querySelector('[data-pb-item], [data-pb-gallery-upload]');
        if (first && first !== itemEl) {
          grid.insertBefore(itemEl, first);
        } else if (!itemEl.isConnected) {
          if (uploadEl) grid.insertBefore(itemEl, uploadEl);
          else grid.appendChild(itemEl);
        }
      }
    }

    prevEl = itemEl;
  }

  if (uploadEl && grid.lastElementChild !== uploadEl) {
    grid.appendChild(uploadEl);
  }

  if (grid.scrollTop !== savedScrollTop) grid.scrollTop = savedScrollTop;
  if (grid.scrollLeft !== savedScrollLeft) grid.scrollLeft = savedScrollLeft;

  const uiSelected = state.ui?.selectedItemId ?? null;
  const previousSelectedId = ctx.selectedId;
  ctx.selectedId = uiSelected && nextIds.has(uiSelected) ? uiSelected : null;

  syncActiveTile(section, ctx.selectedId, previousSelectedId);
  refreshRendererTiers(section, store, ctx);

  if (!ctx.layoutReady) {
    refitGalleryLayouts(section, store, ctx, { instant: true });
    ctx.layoutReady = true;
    requestAnimationFrame(() => {
      refitGalleryLayouts(section, store, ctx, { instant: true });
      if (grid.scrollTop !== savedScrollTop) grid.scrollTop = savedScrollTop;
      if (grid.scrollLeft !== savedScrollLeft) grid.scrollLeft = savedScrollLeft;
    });
    return;
  }

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      refitGalleryLayouts(section, store, ctx, { instant: true });
      if (grid.scrollTop !== savedScrollTop) grid.scrollTop = savedScrollTop;
      if (grid.scrollLeft !== savedScrollLeft) grid.scrollLeft = savedScrollLeft;
    });
  });
}

function formatMoneyCents(cents, adapter) {
  return formatPresentmentMoney(cents, adapter?.money);
}

function fillSummaryTemplate(template, values) {
  let out = template || '';
  for (const [key, value] of Object.entries(values)) {
    out = out.replaceAll(`__${key}__`, String(value));
    out = out.replaceAll(`{{ ${key.toLowerCase()} }}`, String(value));
    out = out.replaceAll(`{{${key.toLowerCase()}}}`, String(value));
  }
  return out;
}

function formatSummaryFrameLabel(frame, family, labels) {
  const raw = String(frame || '').trim();
  if (family === 'canvas') {
    if (!raw || raw.toLowerCase() === 'none') return labels.canvas;
  }
  if (!raw) return family === 'canvas' ? labels.canvas : labels.frame;
  return raw.replace(/\b\w/g, (ch) => ch.toUpperCase());
}

function itemHasAcrylic(item) {
  return item.selectedOptions?.acrylic === true || item.selectedOptions?.acrylic === 'true';
}

function itemAcrylicCents(item, adapter) {
  if (!itemHasAcrylic(item) || !adapter?.variantResolver?.resolveAddOn) return 0;
  return Number(adapter.variantResolver.resolveAddOn(item.selectedOptions)?.priceCents) || 0;
}

function projectFrameOnlyCartItem(store) {
  const state = store.getState();
  if (
    store.adapter?.family !== 'frames' ||
    state.items.length !== 0 ||
    state.computed?.cartCapable !== true
  ) {
    return null;
  }

  try {
    const item = store.buildItemFromSidebar({
      id: '__frame_only_preview__',
      image: null,
    });
    if (!item.variantId || item.availability === 'unavailable') return null;
    if (itemHasAcrylic(item) && store.adapter.acrylicPolicy !== 'included') {
      const addOn = store.adapter.variantResolver?.resolveAddOn?.(item.selectedOptions);
      if (!addOn?.id || addOn.available === false) return null;
    }
    return item;
  } catch (error) {
    pbWarn('frame-only projection failed', error);
    return null;
  }
}

function canRequestPhotoCart(computed) {
  return (
    computed?.cartCapable === true &&
    computed.hasItems === true &&
    computed.imageItemCount > 0 &&
    computed.unavailableCount === 0
  );
}

function buildGroupedSummaryLines(state, linesEl, adapter) {
  const groups = new Map();
  const labels = {
    canvas: linesEl.dataset.pbLabelCanvas,
    frame: linesEl.dataset.pbLabelFrame,
  };
  for (const item of state.items) {
    const sizeDisplay = formatItemSizeLabel(item) || item.selectedOptions?.size || '—';
    const frameRaw = item.selectedOptions?.frame ?? '';
    const frameDisplay = formatSummaryFrameLabel(frameRaw, state.family, labels);
    const hasAcrylic = itemHasAcrylic(item);
    const groupKey = `${String(frameRaw).toLowerCase()}|${sizeDisplay}|${hasAcrylic ? 'acrylic' : 'no-acrylic'}`;
    const lineCents = (item.priceCents || 0) + itemAcrylicCents(item, adapter);
    const existing = groups.get(groupKey);
    if (existing) {
      existing.count += 1;
      existing.priceCents += lineCents;
    } else {
      groups.set(groupKey, {
        count: 1,
        frameDisplay,
        sizeDisplay,
        priceCents: lineCents,
      });
    }
  }

  const isCanvas = state.family === 'canvas';
  const template = isCanvas
    ? linesEl.dataset.pbLineCanvas
    : linesEl.dataset.pbLineFrames;
  for (const group of groups.values()) {
    const label = fillSummaryTemplate(template, {
      COUNT: group.count,
      FRAME: group.frameDisplay,
      SIZE: group.sizeDisplay,
      ACRYLIC: '',
      count: group.count,
      frame: group.frameDisplay,
      size: group.sizeDisplay,
      acrylic: '',
    });
    appendSummaryLine(linesEl, label, formatMoneyCents(group.priceCents, adapter));
  }
}

function appendSummaryLine(linesEl, label, value, className) {
  const row = document.createElement('div');
  row.className = className ? `pb-summary__line ${className}` : 'pb-summary__line';
  const labelEl = document.createElement('span');
  labelEl.className = 'pb-summary__line-label';
  labelEl.textContent = label;
  const valueEl = document.createElement('span');
  valueEl.className = 'pb-summary__line-value';
  valueEl.textContent = value;
  row.append(labelEl, valueEl);
  linesEl.appendChild(row);
}

function formatSummaryAtcLabel(labelEl, priceText) {
  const plain = labelEl.dataset.pbAdd || labelEl.dataset.pbDefault;
  if (!priceText) return plain;
  const template = labelEl.dataset.pbAddWithPrice || '';
  if (template.includes('__PRICE__')) {
    return template.replaceAll('__PRICE__', priceText);
  }
  if (template.includes('{{ price }}')) {
    return template.replaceAll('{{ price }}', priceText);
  }
  return `${plain} • ${priceText}`;
}

function renderSummary(section, store) {
  const state = store.getState();
  const finalEl = section.querySelector('[data-pb-summary-final]');
  const compareEl = section.querySelector('[data-pb-summary-compare]');
  const linesEl = section.querySelector('[data-pb-summary-lines]');
  const savingsEl = section.querySelector('[data-pb-summary-savings]');
  const savingsAmountEl = section.querySelector('[data-pb-summary-savings-amount]');
  const c = state.computed;
  const frameOnlyItem = projectFrameOnlyCartItem(store);
  const frameOnlyGrossCents = frameOnlyItem
    ? (Number(frameOnlyItem.priceCents) || 0) + itemAcrylicCents(frameOnlyItem, store.adapter)
    : 0;
  const discountPct = Number(store.adapter?.discount?.percent) || 0;
  const discountThresholdCents = Number(store.adapter?.discount?.thresholdCents) || 0;
  const frameOnlySavingsCents =
    frameOnlyItem && discountPct > 0 && frameOnlyGrossCents >= discountThresholdCents
      ? Math.round((frameOnlyGrossCents * discountPct) / 100)
      : 0;
  const grossCents = frameOnlyItem ? frameOnlyGrossCents : c.grossCents;
  const savingsCents = frameOnlyItem ? frameOnlySavingsCents : c.savingsCents;
  const subtotalCents = frameOnlyItem
    ? Math.max(0, frameOnlyGrossCents - frameOnlySavingsCents)
    : c.subtotalCents;
  const hasSavings = savingsCents > 0;
  const priceText =
    subtotalCents > 0
      ? formatMoneyCents(subtotalCents, store.adapter)
      : frameOnlyItem?.priceFormatted || state.items[0]?.priceFormatted || '';

  if (finalEl) {
    if (subtotalCents > 0) {
      finalEl.textContent = formatMoneyCents(subtotalCents, store.adapter);
    } else if (frameOnlyItem?.priceFormatted) {
      finalEl.textContent = frameOnlyItem.priceFormatted;
    } else if (state.items[0]?.priceFormatted) {
      finalEl.textContent = state.items[0].priceFormatted;
    } else {
      finalEl.textContent = formatMoneyCents(0, store.adapter);
    }
  }

  if (compareEl) {
    if (hasSavings && grossCents > 0) {
      compareEl.hidden = false;
      compareEl.textContent = formatMoneyCents(grossCents, store.adapter);
    } else {
      compareEl.hidden = true;
      compareEl.textContent = '';
    }
  }

  if (savingsEl) {
    savingsEl.hidden = !hasSavings;
    if (savingsAmountEl && hasSavings) {
      savingsAmountEl.textContent = formatMoneyCents(savingsCents, store.adapter);
    }
  }

  if (linesEl) {
    linesEl.replaceChildren();
    const summaryState = frameOnlyItem ? { ...state, items: [frameOnlyItem] } : state;
    buildGroupedSummaryLines(summaryState, linesEl, store.adapter);

    if (hasSavings) {
      const discountTemplate = linesEl.dataset.pbLineDiscount;
      const discountLabel = fillSummaryTemplate(discountTemplate, {
        PERCENT: discountPct,
        percent: discountPct,
      });
      appendSummaryLine(
        linesEl,
        discountLabel,
        `-${formatMoneyCents(savingsCents, store.adapter)}`,
        'pb-summary__line--discount'
      );
    }
  }

  const atcBtn = section.querySelector('[data-pb-summary-atc]');
  if (atcBtn instanceof HTMLButtonElement && atcBtn.dataset.pbSubmitting !== 'true') {
    const canOrderFrameOnly = Boolean(frameOnlyItem);
    const awaitingUploads = atcBtn.dataset.pbAwaitingUploads === 'true';
    const canRequestPhotoSubmit = canRequestPhotoCart(c);
    atcBtn.disabled =
      awaitingUploads || !(c.canAddToCart || canRequestPhotoSubmit || canOrderFrameOnly);
    atcBtn.dataset.pbFrameOnlyReady = canOrderFrameOnly ? 'true' : '';
    const frameOnlyDialog = section.querySelector('[data-pb-frame-only-dialog]');
    if (canOrderFrameOnly && frameOnlyDialog?.id) {
      atcBtn.setAttribute('aria-haspopup', 'dialog');
      atcBtn.setAttribute('aria-controls', frameOnlyDialog.id);
    } else {
      atcBtn.removeAttribute('aria-haspopup');
      atcBtn.removeAttribute('aria-controls');
    }
    const labelEl = section.querySelector('[data-pb-summary-atc-label]');
    if (labelEl) {
      if (!labelEl.dataset.pbDefault) {
        labelEl.dataset.pbDefault = labelEl.dataset.pbAdd || labelEl.textContent;
      }
      if (atcBtn.dataset.pbReloadRequired === 'true') {
        labelEl.textContent = labelEl.dataset.pbReloadToContinue || labelEl.dataset.pbTryAgain;
      } else if (atcBtn.dataset.pbSubmitError) {
        labelEl.textContent = atcBtn.dataset.pbSubmitError;
      } else if (!c.cartCapable) {
        labelEl.textContent = labelEl.dataset.pbCanvasUnavailable;
      } else if (awaitingUploads) {
        const uploadingLabel = labelEl.dataset.pbUploading;
        labelEl.textContent = `${uploadingLabel} ${c.sourceUploadedCount}/${c.imageItemCount}`;
      } else {
        // Desktop Full Summary design uses the plain ATC label; mobile keeps price.
        const usePriceLabel = isMobileLayout();
        labelEl.textContent = usePriceLabel
          ? formatSummaryAtcLabel(labelEl, priceText)
          : labelEl.dataset.pbAdd;
        labelEl.dataset.pbDefault = labelEl.textContent;
      }
    }
  }
}

function wireCartAtc(section, store) {
  const atcBtn = section.querySelector('[data-pb-summary-atc]');
  if (!(atcBtn instanceof HTMLButtonElement) || atcBtn.dataset.pbAtcWired === 'true') return;
  atcBtn.dataset.pbAtcWired = 'true';

  const labelEl = section.querySelector('[data-pb-summary-atc-label]');
  const frameOnlyDialog = section.querySelector('[data-pb-frame-only-dialog]');
  const frameOnlyUpload = section.querySelector('[data-pb-frame-only-upload]');
  const frameOnlyConfirm = section.querySelector('[data-pb-frame-only-confirm]');
  // Invalidates in-flight ATC after BFCache restore so a late success cannot clear a
  // new upload session the customer started after pressing Back.
  let submitGeneration = 0;
  const loadCartModule = createCartModuleLoader({
    importModule: specifier => import(specifier),
    resolveUrl: () => import.meta.resolve('builder-cart'),
  });
  // Warm dependencies while originals are uploading, and retain a bounded recovery path.
  void loadCartModule().catch(() => {});

  const releaseSubmitLock = () => {
    atcBtn.dataset.pbSubmitting = '';
    atcBtn.dataset.pbAwaitingUploads = '';
    if (frameOnlyConfirm instanceof HTMLButtonElement) frameOnlyConfirm.disabled = false;
    setSubmitLock(section, false);
  };

  const incompleteImageItems = (state) =>
    state.items.filter((row) => row.image && !row.image.source?.filestackHandle);

  const canProgressSourceUploads = (state) => {
    const pipeline = imagePipelines.get(section);
    return incompleteImageItems(state).some((row) => Boolean(pipeline?.getOriginalFile?.(row.id)));
  };

  const releaseAwaitingUploads = (reason) => {
    if (atcBtn.dataset.pbAwaitingUploads !== 'true') return;
    pbWarn('releasing awaiting-uploads lock', reason);
    releaseSubmitLock();
    renderSummary(section, store);
  };

  /** Kick the upload manager when ATC is waiting on Filestack. */
  const armAwaitingUploads = async () => {
    atcBtn.dataset.pbAwaitingUploads = 'true';
    setSubmitLock(section, true);
    renderSummary(section, store);

    const ensure = uploadManagerEnsures.get(section);
    let manager = null;
    try {
      manager = ensure ? await ensure() : null;
    } catch (error) {
      console.error('[product-builder] upload manager failed while awaiting uploads', error);
      releaseAwaitingUploads('upload manager failed to start');
      globalThis.window?.reportToWorkManager?.(new Error('Upload manager startup failed'), { provider: 'filestack', operation: 'initialize', stage: 'cart_readiness', kind: 'startup', classificationStatus: 'failed' });
      return;
    }
    if (atcBtn.dataset.pbAwaitingUploads !== 'true') return;

    if (!manager || manager.uploadsEnabled === false) {
      console.error(
        '[product-builder] Filestack appKey missing — source uploads cannot run; refusing awaiting-uploads lock'
      );
      releaseAwaitingUploads('Filestack uploads disabled (missing appKey)');
      if (manager?.uploadsEnabled === false) globalThis.window?.reportToWorkManager?.(new Error('Upload configuration unavailable'), { provider: 'filestack', operation: 'initialize', stage: 'cart_readiness', kind: 'configuration', classificationStatus: 'failed' });
      return;
    }

    try {
      // Force the same recovery as BFCache resume: clear zombie inFlight rows and
      // re-queue interrupted uploads. Idempotent start() alone only pumps.
      if (typeof manager.resumeAfterPageShow === 'function') {
        await manager.resumeAfterPageShow();
      } else {
        manager.start?.();
      }
    } catch (error) {
      console.error('[product-builder] upload manager start failed while awaiting', error);
      releaseAwaitingUploads('upload manager start failed');
      globalThis.window?.reportToWorkManager?.(new Error('Upload manager startup failed'), { provider: 'filestack', operation: 'initialize', stage: 'cart_readiness', kind: 'startup', classificationStatus: 'failed' });
      return;
    }

    if (!canProgressSourceUploads(store.getState())) {
      console.error(
        '[product-builder] source uploads cannot progress — no in-session originals for incomplete items'
      );
      releaseAwaitingUploads('no in-session source files left to upload');
      globalThis.window?.reportToWorkManager?.(new Error('Upload originals unavailable'), { provider: 'filestack', operation: 'upload', stage: 'cart_readiness', kind: 'missing_source', classificationStatus: 'failed' });
    }
  };

  const recoverAtcAfterPageShow = (event) => {
    // Initial pageshow can arrive after a customer already pressed ATC (slow
    // page resources). Only BFCache restoration invalidates an existing submit.
    if (!event.persisted) return;
    submitGeneration += 1;
    releaseSubmitLock();
    syncBootUi(section, store);
  };

  const submit = async (frameOnly) => {
    if (atcBtn.dataset.pbSubmitting === 'true') return;
    const generation = ++submitGeneration;
    atcBtn.dataset.pbSubmitting = 'true';
    atcBtn.dataset.pbAwaitingUploads = '';
    atcBtn.disabled = true;
    if (frameOnlyConfirm instanceof HTMLButtonElement) frameOnlyConfirm.disabled = true;
    setSubmitLock(section, true);
    if (labelEl) labelEl.textContent = labelEl.dataset.pbAdding;

    try {
      let mod;
      try { mod = await loadCartModule().catch(() => loadCartModule()); } catch (error) {
        if (generation === submitGeneration && navigator.onLine !== false) atcBtn.dataset.pbReloadRequired = 'true';
        throw error;
      }
      if (generation !== submitGeneration) return;
      const isActive = () => generation === submitGeneration;
      const result = frameOnly
        ? await mod.submitFrameOnlyCart({ store, isActive, log: pbLog, warn: pbWarn })
        : await mod.submitCart({ store, isActive, log: pbLog, warn: pbWarn });
      if (generation !== submitGeneration) return;
      if (result.ok) return;
      if (result.reason === 'abandoned') return;
      if (labelEl) labelEl.textContent = labelEl.dataset.pbCartPartialError;
    } catch (error) {
      if (generation !== submitGeneration) return;
      pbWarn('cart submit failed', error);
      if (labelEl) labelEl.textContent = labelEl.dataset.pbTryAgain;
      globalThis.window?.reportToWorkManager?.(new Error('Cart submission failed'), window.reportToWorkManager.cartContext(error, 'cart_add', 'add_to_cart'));
    }

    if (generation !== submitGeneration) return;
    atcBtn.dataset.pbSubmitError = labelEl?.textContent || '';
    releaseSubmitLock();
    renderSummary(section, store);
    atcBtn.disabled = !(
      store.getState().computed.canAddToCart ||
      canRequestPhotoCart(store.getState().computed) ||
      projectFrameOnlyCartItem(store)
    );
    setTimeout(() => {
      if (generation !== submitGeneration) return;
      if (atcBtn.dataset.pbSubmitting === 'true') return;
      renderSummary(section, store);
    }, 3000);
  };

  atcBtn.addEventListener('click', () => {
    if (atcBtn.dataset.pbReloadRequired === 'true') {
      // All source handles are already saved before submit. Do not refresh on failed persistence.
      void withDeadline(() => store.flush(), 10000).then(() => {
        if (store.canReloadSafely?.()) window.location.reload();
        else pbWarn('Draft persistence is unavailable; keeping the current page');
      }).catch(error => pbWarn('Could not save before reload', error));
      return;
    }
    delete atcBtn.dataset.pbSubmitError;
    const frameOnlyItem = projectFrameOnlyCartItem(store);
    if (frameOnlyItem) {
      if (frameOnlyDialog instanceof HTMLElement) {
        openDialog(frameOnlyDialog);
        requestAnimationFrame(() => {
          if (frameOnlyUpload instanceof HTMLButtonElement) frameOnlyUpload.focus();
        });
      } else {
        pbWarn('frame-only dialog is missing');
      }
      return;
    }
    const computed = store.getState().computed;
    if (!computed.canAddToCart) {
      if (!canRequestPhotoCart(computed) || computed.allUploaded) return;
      if (section.dataset.pbFilestackConfigured !== 'true') {
        console.error(
          '[product-builder] Filestack appKey missing — cannot upload sources or add photo cart'
        );
        globalThis.window?.reportToWorkManager?.(new Error('Upload configuration unavailable'), { provider: 'filestack', operation: 'initialize', stage: 'cart_readiness', kind: 'configuration', classificationStatus: 'failed' });
        return;
      }
      void armAwaitingUploads();
      return;
    }
    void submit(false);
  });

  if (frameOnlyUpload instanceof HTMLButtonElement) {
    frameOnlyUpload.addEventListener('click', () => {
      if (frameOnlyDialog instanceof HTMLElement) closeDialog(frameOnlyDialog);
      section.querySelector('[data-pb-file-input]')?.click();
    });
  }

  if (frameOnlyConfirm instanceof HTMLButtonElement) {
    frameOnlyConfirm.addEventListener('click', () => {
      if (!projectFrameOnlyCartItem(store)) {
        if (frameOnlyDialog instanceof HTMLElement) closeDialog(frameOnlyDialog);
        renderSummary(section, store);
        return;
      }
      if (frameOnlyDialog instanceof HTMLElement) closeDialog(frameOnlyDialog);
      void submit(true);
    });
  }

  const pendingSubmitSubscription = store.subscribe((state) => {
    if (atcBtn.dataset.pbAwaitingUploads !== 'true') return;
    if (state.computed.canAddToCart) {
      void submit(false);
      return;
    }
    if (!canRequestPhotoCart(state.computed)) {
      releaseSubmitLock();
      renderSummary(section, store);
      return;
    }
    // Sources still missing handles but nothing left that can upload (originals
    // purged / never retained) — don't sit on "Uploading photos… 0/n" forever.
    if (imagePipelines.get(section)?.isRestoringSources?.()) return;
    if (incompleteImageItems(state).length > 0 && !canProgressSourceUploads(state)) {
      releaseAwaitingUploads('source uploads cannot progress');
    }
  });
  window.addEventListener('pageshow', recoverAtcAfterPageShow);
  registerSectionCleanup(section, () => {
    pendingSubmitSubscription.unsubscribe();
    window.removeEventListener('pageshow', recoverAtcAfterPageShow);
    submitGeneration += 1;
  });
}

function wireSummaryDisclosure(section) {
  const toggle = section.querySelector('.pb-summary__toggle-input');
  const button = section.querySelector('[data-pb-summary-toggle]');
  if (
    !(toggle instanceof HTMLInputElement) ||
    !(button instanceof HTMLButtonElement) ||
    button.dataset.pbDisclosureWired === 'true'
  ) {
    return;
  }
  button.dataset.pbDisclosureWired = 'true';
  const sync = () => {
    button.setAttribute('aria-expanded', toggle.checked ? 'true' : 'false');
  };
  button.addEventListener('click', () => {
    toggle.checked = !toggle.checked;
    toggle.dispatchEvent(new Event('change', { bubbles: true }));
  });
  toggle.addEventListener('change', sync);
  sync();
}

function readGalleryI18n(section) {
  const node = section.querySelector('[data-pb-i18n]');
  if (!node) return {};
  try {
    return JSON.parse(node.textContent || '{}');
  } catch (error) {
    pbWarn('readGalleryI18n: failed to parse translation JSON', error);
    return {};
  }
}

function wireGalleryUpload(section, pipeline) {
  if (section.dataset.pbUploadWired === 'true') return;

  const fileInput = section.querySelector('[data-pb-file-input]');
  if (!fileInput || !pipeline) return;

  section.dataset.pbUploadWired = 'true';

  fileInput.addEventListener('change', () => {
    const files = fileInput.files;
    if (!files?.length) return;
    pipeline.ingestFiles(files);
    fileInput.value = '';
  });
}

function syncSizeChipPrices(section, store) {
  const sizeBlock = section.querySelector('[data-pb-price-each-template]');
  if (!sizeBlock) return;

  const template = sizeBlock.getAttribute('data-pb-price-each-template') || '';
  const selected = store.getState().selectedOptions;
  sizeBlock.querySelectorAll('[data-pb-chip]').forEach((chip) => {
    const meta = chip.querySelector('.pb-chip__meta');
    if (!meta) return;
    const label = formatSizeCardPriceLabel(
      resolveSizeCardPrice(store.adapter, selected, chip.getAttribute('data-value')),
      template
    );
    if (label) meta.textContent = label;
  });
}

function syncChipsFromStore(section, store) {
  const selected = store.getState().selectedOptions;
  const sizeDisplay = getActiveSizeDisplay(store);

  section.querySelectorAll('[data-pb-option]').forEach((optionBlock) => {
    const optionId = optionBlock.getAttribute('data-pb-option');
    if (!optionId) return;

    if (optionId === 'bw') {
      const checked = store.getState().imageDefaults?.bw === true;
      optionBlock.querySelector('[data-pb-bw-checkbox]')?.setAttribute('aria-checked', String(checked));
      optionBlock.querySelector('[data-pb-bw-toggle]')?.setAttribute('aria-pressed', String(checked));
      return;
    }

    const value = selected[optionId];
    if (value == null) return;

    const chips = [...optionBlock.querySelectorAll('[data-pb-chip]')];
    const match =
      optionId === 'size'
        ? findSizeChip(chips, String(value), sizeDisplay)
        : chips.find((chip) => chip.getAttribute('data-value') === String(value)) ?? chips[0];

    if (match) {
      chips.forEach((chip) => {
        chip.setAttribute('aria-pressed', chip === match ? 'true' : 'false');
      });
      const currentEl = optionBlock.querySelector(`[data-pb-option-current="${optionId}"]`);
      if (currentEl) {
        currentEl.textContent = match.querySelector('.pb-chip__value')?.textContent?.trim() || String(value);
      }
    }
  });

  syncSizeChipPrices(section, store);
}

function wireStoreUi(section, store, payload, adapter) {
  if (section.dataset.pbStoreReady === 'true') return;
  section.dataset.pbStoreReady = 'true';

  let lastSelectedKey = JSON.stringify(store.getState().selectedOptions);
  let applyToAllPrompt = null;
  /**
   * @param {string} optionId
   * @returns {Promise<{ proceed: boolean, applyToAll: boolean }>}
   */
  const resolveApplyToAll = async (optionId) => {
    const items = store.getState().items.filter((item) => optionId !== 'bw' || item.image);
    if (!items.length) return { proceed: true, applyToAll: false };
    const readValue = (item) => optionId === 'bw'
      ? item.image?.bw === true
      : item.selectedOptions?.[optionId];
    const firstValue = readValue(items[0]);
    const mixed = items.some((item) => readValue(item) !== firstValue);
    if (!mixed) return { proceed: true, applyToAll: true };
    if (applyToAllPrompt) return { proceed: false, applyToAll: false };
    applyToAllPrompt = promptApplyToAll(section);
    try {
      const accepted = await applyToAllPrompt;
      if (!accepted) return { proceed: false, applyToAll: false };
      return { proceed: true, applyToAll: true };
    } finally {
      applyToAllPrompt = null;
    }
  };
  const scheduler = createRenderScheduler({
    readState: () => store.getState(),
    render(_state, changeSet) {
      if (changeSet.global.has('selection')) {
        const frame = store.getState().selectedOptions.frame ?? '';
        section.dataset.productFrame = frame ? frameStyleKey(frame) : 'none';
      }

      const shouldSyncGallery =
        changeSet.items.size > 0 ||
        changeSet.structural.added.size > 0 ||
        changeSet.structural.removed.size > 0 ||
        changeSet.structural.orderChanged ||
        changeSet.global.has('selection') ||
        changeSet.global.has('activeItem') ||
        changeSet.global.has('galleryGeometry');
      if (shouldSyncGallery) {
        syncGallery(section, store, changeSet);
      }

      if (changeSet.global.has('summary')) {
        renderSummary(section, store);
      }
      if (changeSet.global.has('selection') || changeSet.global.has('controls')
        || changeSet.structural.added.size || changeSet.structural.removed.size
        || [...changeSet.items.values()].some((domains) => domains.has('style') || domains.has('geometry'))) {
        syncChipsFromStore(section, store);
        updateChipAvailability(section, adapter, store.getState());
      }

      const selectedKey = JSON.stringify(store.getState().selectedOptions);
      if (selectedKey !== lastSelectedKey) {
        lastSelectedKey = selectedKey;
        requestAnimationFrame(() => {
          scrollActiveChipsIntoView(section, { behavior: 'smooth' });
        });
      }

      if (isDebugEnabled()) {
        logDebugState(payload, adapter, store);
      }
    },
  });
  renderSchedulers.set(section, scheduler);

  const storeSubscription = store.subscribe((_state, changeSet) => {
    if (!changeSet) return;
    const renderable =
      changeSet.items.size > 0 ||
      changeSet.structural.added.size > 0 ||
      changeSet.structural.removed.size > 0 ||
      changeSet.structural.orderChanged ||
      ['selection', 'activeItem', 'controls', 'summary', 'galleryGeometry'].some((domain) =>
        changeSet.global.has(domain)
      );
    if (renderable) scheduler.enqueue(changeSet);
  });
  registerSectionCleanup(section, () => {
    storeSubscription.unsubscribe();
    scheduler.destroy();
    renderSchedulers.delete(section);
  });

  section.querySelectorAll('[data-pb-option]').forEach((optionBlock) => {
    const optionId = optionBlock.getAttribute('data-pb-option');
    if (!optionId) return;

    if (optionId === 'bw') {
      optionBlock.querySelectorAll('[data-pb-bw-checkbox], [data-pb-bw-toggle]').forEach((control) => {
        control.addEventListener('click', async () => {
          const decision = await resolveApplyToAll('bw');
          if (!decision.proceed) return;
          store.setBlackAndWhite(store.getState().imageDefaults?.bw !== true);
        });
      });
      return;
    }

    if (optionId === 'frame') {
      optionBlock.addEventListener(
        'mouseover',
        (event) => {
          const chip = event.target.closest('[data-pb-chip]');
          if (!chip || !optionBlock.contains(chip) || chip.disabled) return;
          preloadFrameBorderImage(resolveFrameImg(adapter, chip.getAttribute('data-value') ?? ''));
        },
        { passive: true }
      );
    }

    optionBlock.addEventListener('click', async (event) => {
      const chip = event.target.closest('[data-pb-chip]');
      if (!chip || !optionBlock.contains(chip) || chip.disabled) return;

      const meta = optionId === 'size' ? readSizeDisplay(chip) : {};
      const decision = await resolveApplyToAll(optionId);
      if (!decision.proceed) return;
      meta.applyToAll = decision.applyToAll;
      const accepted = store.setSelectedOption(optionId, chip.getAttribute('data-value') ?? '', meta);
      if (accepted === false) {
        syncChipsFromStore(section, store);
        updateChipAvailability(section, adapter, store.getState());
      }
    });
  });
}

function logCartPropertiesDryRun(adapter, items) {
  if (adapter.family === 'frames') {
    pbLog(
      'cart:dry-run',
      items.map((item) => ({
        id: item.id,
        variantId: item.variantId,
        _border_: item.selectedOptions?.mat ?? 'No Mat',
        _acrylic_: Boolean(item.selectedOptions?.acrylic),
      }))
    );
    return;
  }

  pbLog(
    'cart:dry-run',
    items.map((item) => ({
      id: item.id,
      variantId: item.variantId,
      _edge_: item.selectedOptions?.edge ?? 'Wrapped',
    }))
  );
}

// Lock the gallery's max-height on mobile to whatever it is on first render
// (when sidebar is fully visible). This means nothing that shows/hides below
// the gallery — panels, summary, Full Summary, More Info — can make it grow.
// Re-locks on orientation/resize, visualViewport changes, and BFCache pageshow.

function wireMobileSidebarHeight(section) {
  if (section.dataset.pbSidebarHeightWired === 'true') return;

  const mq = window.matchMedia('(max-width: 867px)');
  const gallery = section.querySelector('.pb-layout__gallery');
  if (!gallery) return;

  section.dataset.pbSidebarHeightWired = 'true';

  let locked = false;

  const lock = () => {
    locked = false;
    gallery.style.removeProperty('max-height');
    if (!mq.matches) {
      section.style.removeProperty('--pb-visible-height');
      return;
    }

    // Prefer the live visual viewport after BFCache / mobile chrome changes —
    // plain 100dvh can leave a cream gap under the sticky summary.
    const vv = window.visualViewport;
    if (vv && vv.height > 0) {
      section.style.setProperty('--pb-visible-height', `${Math.round(vv.height)}px`);
    } else {
      section.style.removeProperty('--pb-visible-height');
    }

    // Wait two frames so layout is fully settled before measuring
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const h = Math.round(gallery.getBoundingClientRect().height);
        if (h > 0) {
          gallery.style.maxHeight = `${h}px`;
          locked = true;
        }
      });
    });
  };

  lock();

  // Re-lock on orientation / window resize (screen dimensions changed)
  let resizeTimer = 0;
  const onResize = () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(lock, 120);
  };
  const onPageShow = (event) => {
    // BFCache Back often changes mobile chrome / visual viewport without a resize.
    if (event.persisted || mq.matches) lock();
  };
  window.addEventListener('resize', onResize, { passive: true });
  window.addEventListener('pageshow', onPageShow);
  window.visualViewport?.addEventListener('resize', onResize, { passive: true });

  mq.addEventListener('change', lock);
  registerSectionCleanup(section, () => {
    window.removeEventListener('resize', onResize);
    window.removeEventListener('pageshow', onPageShow);
    window.visualViewport?.removeEventListener('resize', onResize);
    mq.removeEventListener('change', lock);
    if (resizeTimer) window.clearTimeout(resizeTimer);
    section.style.removeProperty('--pb-visible-height');
    gallery.style.removeProperty('max-height');
  });
}

function wireCollapsiblePanels(section, store) {
  if (section.dataset.pbPanelsReady === 'true') return;
  section.dataset.pbPanelsReady = 'true';

  /** @type {HTMLDetailsElement[]} */
  const panels = [...section.querySelectorAll('details.pb-collapsible[data-pb-info-block]')].filter(
    (el) => el instanceof HTMLDetailsElement && el.getAttribute('data-pb-panel-id')
  );

  const savedPanels = store.getState().ui?.panels ?? {};
  const hasSavedOpen = panels.some((panel) => savedPanels[panel.getAttribute('data-pb-panel-id')] === true);

  // Exclusive accordion: at most one open after restore / open_by_default markup.
  if (hasSavedOpen) {
    let opened = false;
    for (const panel of panels) {
      const panelId = panel.getAttribute('data-pb-panel-id');
      const shouldOpen = !opened && savedPanels[panelId] === true;
      panel.open = shouldOpen;
      if (shouldOpen) opened = true;
    }
  } else {
    let seenOpen = false;
    for (const panel of panels) {
      if (!panel.open) continue;
      if (seenOpen) panel.open = false;
      else seenOpen = true;
    }
  }

  for (const panel of panels) {
    const panelId = panel.getAttribute('data-pb-panel-id');
    if (!panelId) continue;

    panel.addEventListener('toggle', () => {
      if (panel.open) {
        for (const other of panels) {
          if (other !== panel && other.open) other.open = false;
        }
      }
      store.setPanelOpen(panelId, panel.open);
    });
  }
}

/** Portal info blocks into the More info sheet body while the sheet is open. */
function wireMoreInfoSheet(section) {
  const toggle = section.querySelector('.pb-more-info__toggle');
  const body = section.querySelector('[data-pb-more-info-body]');
  const scroll = section.querySelector('[data-pb-sidebar-scroll]');
  if (
    !(toggle instanceof HTMLInputElement) ||
    !(body instanceof HTMLElement) ||
    !(scroll instanceof HTMLElement) ||
    toggle.dataset.pbMoreInfoWired === 'true'
  ) {
    return;
  }
  toggle.dataset.pbMoreInfoWired = 'true';

  /** @type {Array<{ node: Element, placeholder: Comment }>} */
  let moved = [];

  const isInfoChild = (el) =>
    el instanceof Element &&
    (el.hasAttribute('data-pb-info-block') || Boolean(el.querySelector('[data-pb-info-block]')));

  const sync = () => {
    if (toggle.checked) {
      if (moved.length) return;
      [...scroll.children].filter(isInfoChild).forEach((node) => {
        const placeholder = document.createComment('pb-more-info');
        node.parentNode?.insertBefore(placeholder, node);
        body.appendChild(node);
        moved.push({ node, placeholder });
      });
      return;
    }

    moved.forEach(({ node, placeholder }) => {
      placeholder.parentNode?.insertBefore(node, placeholder);
      placeholder.remove();
    });
    moved = [];
  };

  toggle.addEventListener('change', sync);
  sync();
}

function addDebugControls(section, store, adapter) {
  const gallery = section.querySelector('[data-pb-gallery]');
  if (!gallery || gallery.querySelector('[data-pb-debug-add]')) return;

  const bar = document.createElement('div');
  bar.className = 'pb-debug-bar';
  bar.setAttribute('data-pb-debug-bar', '');

  const addBtn = document.createElement('button');
  addBtn.type = 'button';
  addBtn.className = 'pb-debug-bar__btn';
  addBtn.setAttribute('data-pb-debug-add', '');
  addBtn.textContent = 'Add placeholder item';

  addBtn.addEventListener('click', () => {
    const sizeChip = section.querySelector('[data-pb-option="size"] [data-pb-chip][aria-pressed="true"]');
    const meta = sizeChip ? readSizeDisplay(sizeChip) : {};
    const item = store.addIngestItem(meta);
    pbLog('item:added', item);
  });

  const addManyBtn = document.createElement('button');
  addManyBtn.type = 'button';
  addManyBtn.className = 'pb-debug-bar__btn';
  addManyBtn.setAttribute('data-pb-debug-add-many', '');
  addManyBtn.textContent = 'Add 50 placeholders';

  addManyBtn.addEventListener('click', () => {
    const sizeChip = section.querySelector('[data-pb-option="size"] [data-pb-chip][aria-pressed="true"]');
    const meta = sizeChip ? readSizeDisplay(sizeChip) : {};
    for (let i = 0; i < 50; i += 1) {
      store.addIngestItem(meta);
    }
    pbLog('item:added', { count: 50 });
  });

  const dryRunBtn = document.createElement('button');
  dryRunBtn.type = 'button';
  dryRunBtn.className = 'pb-debug-bar__btn';
  dryRunBtn.setAttribute('data-pb-debug-cart-dry-run', '');
  dryRunBtn.textContent = 'Log cart properties dry-run';

  dryRunBtn.addEventListener('click', () => {
    logCartPropertiesDryRun(adapter, store.getState().items);
  });

  bar.appendChild(addBtn);
  bar.appendChild(addManyBtn);
  bar.appendChild(dryRunBtn);
  gallery.prepend(bar);
}

/**
 * Sync shell UI from the current store (gallery, summary, chips, frame attr).
 * Used after progressive hydrate/restore — path-specific subscribers do not
 * receive hydrate's notify(['*']).
 */
function syncBootUi(section, store) {
  const frame = store.getState().selectedOptions.frame ?? '';
  section.dataset.productFrame = frame ? frameStyleKey(frame) : 'none';
  syncGallery(section, store);
  renderSummary(section, store);
  syncChipsFromStore(section, store);
}

function rememberServerShell(section) {
  if (!window.Shopify?.designMode) return;
  if (sectionShellMarkup.has(section)) return;
  sectionShellMarkup.set(section, section.innerHTML);
}

function restoreServerShell(section) {
  const markup = sectionShellMarkup.get(section);
  if (!markup) return false;
  section.innerHTML = markup;
  return true;
}

/** Regions that mutate store state — locked during hydrate/restore and ATC submit. */
const MUTATION_LOCK_SELECTORS = [
  '[data-pb-layout-gallery]',
  '[data-pb-product-options]',
  '[data-pb-info-block]',
  '[data-pb-summary-atc]',
];

/**
 * Apply inert/aria from hydrate + submit lock classes.
 * Non-mutating chrome (More info / summary expand) stays outside these roots.
 */
function applyMutationLocks(section) {
  if (!(section instanceof HTMLElement)) return;

  const hydrating = section.classList.contains('pb-builder--hydrating');
  const submitting = section.classList.contains('pb-builder--submitting');
  const locked = hydrating || submitting;

  if (locked) section.setAttribute('aria-busy', 'true');
  else section.removeAttribute('aria-busy');

  for (const selector of MUTATION_LOCK_SELECTORS) {
    section.querySelectorAll(selector).forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      if (hydrating) node.setAttribute('data-pb-hydrate-lock', '');
      else node.removeAttribute('data-pb-hydrate-lock');
      if (submitting) node.setAttribute('data-pb-submit-lock', '');
      else node.removeAttribute('data-pb-submit-lock');
      node.inert = locked;
    });
  }
}

/** Temporarily disable mutating controls while IndexedDB restores (with shimmer). */
function setHydrationLock(section, locked) {
  if (!(section instanceof HTMLElement)) return;
  section.classList.toggle('pb-builder--hydrating', locked);
  applyMutationLocks(section);
}

/** Disable mutating controls while ATC is waiting on uploads or submitting. */
function setSubmitLock(section, locked) {
  if (!(section instanceof HTMLElement)) return;
  section.classList.toggle('pb-builder--submitting', locked);
  applyMutationLocks(section);
}

function resolveBuilderSection(scope) {
  if (!(scope instanceof Element)) return null;
  if (scope.matches('[data-pb-section]')) return scope;
  return scope.querySelector('[data-pb-section]');
}

function cleanupSectionRuntime(section) {
  if (editorOpenSection === section) cancelPendingEditorOpen();
  void closeEditorSessionForSection(section).catch(() => {});
  forgetCarousels((carousel) => !carousel.isConnected || section.contains(carousel));
  const cleanups = sectionCleanups.get(section);
  if (cleanups) {
    for (const cleanup of [...cleanups].reverse()) {
      try {
        cleanup();
      } catch (error) {
        pbWarn('section cleanup failed', error);
      }
    }
    cleanups.clear();
    sectionCleanups.delete(section);
  }

  destroyGalleryContext(section);
  renderSchedulers.get(section)?.destroy();
  renderSchedulers.delete(section);
  imagePipelines.get(section)?.destroy?.();
  imagePipelines.delete(section);
  builderCatalogs.delete(section);
  stores.get(section)?.destroy?.();
  stores.delete(section);
  setHydrationLock(section, false);
  setSubmitLock(section, false);

  for (const key of [
    'pbAppReady',
    'pbStoreReady',
    'pbBooting',
    'pbBootAttempt',
    'pbBootFailed',
    'pbBootStage',
    'pbUploadWired',
    'pbPanelsReady',
    'pbSidebarHeightWired',
  ]) {
    delete section.dataset[key];
  }
}

function destroySection(section) {
  const attempt = bootAttempts.get(section);
  if (attempt) {
    attempt.disposed = true;
    bootAttempts.delete(section);
  }
  cleanupSectionRuntime(section);
  restoreServerShell(section);
}

/**
 * Boot strategy (progressive paint + mutation lock):
 * 1. Render the server shell immediately and wire the core UI.
 * 2. Lock only mutating controls while persisted core state hydrates.
 * 3. Mark ready once variant, pricing, frame, text, and controls are correct.
 * 4. Restore persisted image URLs and optional upload support independently.
 */
function isActiveBootAttempt(section, attempt) {
  return !attempt.disposed && section.isConnected && bootAttempts.get(section) === attempt;
}

function isCurrentRuntime(section, attempt, store) {
  return !attempt.disposed && section.isConnected && stores.get(section) === store;
}

function bootFailureCategory(stage) {
  if (stage === 'payload' || stage === 'adapter' || stage === 'variant') return 'configuration';
  if (stage === 'hydrate') return 'state';
  if (stage === 'shell' || stage === 'core-sync' || stage === 'refresh') return 'rendering';
  return 'programming';
}

function bootSection(section) {
  rememberServerShell(section);

  const activeAttempt = bootAttempts.get(section);
  if (activeAttempt && !activeAttempt.disposed) return activeAttempt.promise;

  const attempt = {
    id: ++nextBootAttemptId,
    disposed: false,
    failed: false,
    stage: 'scheduled',
    promise: null,
  };
  section.dataset.pbBootAttempt = String(attempt.id);
  bootAttempts.set(section, attempt);
  attempt.promise = Promise.resolve().then(() => runBootAttempt(section, attempt));
  return attempt.promise;
}

async function restoreSecondaryImages(section, attempt, store, pipeline) {
  if (!isCurrentRuntime(section, attempt, store)) return;

  try {
    await pipeline.restoreThumbsFromIdb();
  } catch (error) {
    reportBuilderFailure(section, error, {
      stage: 'image-thumbnail-restore',
      category: 'secondary-image',
      fatal: false,
    });
  }
  if (!isCurrentRuntime(section, attempt, store)) return;

  try {
    await pipeline.restoreDisplayUrlsFromIdb();
  } catch (error) {
    reportBuilderFailure(section, error, {
      stage: 'image-display-restore',
      category: 'secondary-image',
      fatal: false,
    });
  }
}

async function runBootAttempt(section, attempt) {
  section.dataset.pbBooting = 'true';

  try {
    if (section.dataset.pbAppReady === 'true') {
      attempt.stage = 'refresh';
      const store = stores.get(section);
      if (!store) throw new Error('Product builder: ready section is missing its store');
      const pipeline = imagePipelines.get(section);
      const payload = loadPayload();
      assertPayload(payload);
      builderCatalogs.set(section, payload.catalog);
      assertVariant(store.adapter, store.getState().selectedOptions);
      initShell({ section, payload, adapter: store.adapter, store, refresh: true });
      if (pipeline) wireGalleryUpload(section, pipeline);
      syncChipsFromStore(section, store);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollActiveChipsIntoView(section);
        });
      });
      return;
    }

    setHydrationLock(section, true);

    attempt.stage = 'payload';
    const payload = loadPayload();
    assertPayload(payload);
    builderCatalogs.set(section, payload.catalog);

    attempt.stage = 'adapter';
    const adapter = createAdapterFromPayload(payload);
    const entrySelectedOptions = resolveEntrySelectedOptions(payload);

    attempt.stage = 'store';
    const store = createStore(payload.family, adapter, section, {
      entrySelectedOptions,
    });

    store.registerMigration({
      fromVersion: 0,
      toVersion: 1,
      migrate: (old) => ({
        ...old,
        schemaVersion: 1,
        items: old.items ?? [],
        selectedOptions: old.selectedOptions ?? {},
        ui: old.ui ?? { selectedItemId: null, panels: {} },
      }),
    });

    stores.set(section, store);

    const statusEl = section.querySelector('[data-pb-upload-status]');
    const i18n = readGalleryI18n(section);
    const reportedImageFailures = new Set();
    const pipeline = createImagePipeline({
      store,
      adapter,
      blobUrls: store.blobUrls,
      strings: i18n,
      workerUrl: payload.image?.workerUrl,
      onError: (error, details = {}) => {
        const stage = details.stage ?? 'image-pipeline';
        const itemId = details.itemId ?? null;
        const failureKey = `${stage}:${itemId ?? 'unknown'}`;
        if (reportedImageFailures.has(failureKey)) return;
        reportedImageFailures.add(failureKey);
        reportBuilderFailure(section, error, {
          stage,
          category: 'secondary-image',
          fatal: false,
          itemId,
        });
      },
      onStatus: (message) => {
        if (!statusEl) return;
        statusEl.textContent = message;
        statusEl.hidden = !message;
      },
    });
    store.setImagePipeline(pipeline);
    imagePipelines.set(section, pipeline);
    const isCurrentBoot = () =>
      isActiveBootAttempt(section, attempt) && stores.get(section) === store;
    const isRuntimeCurrent = () => isCurrentRuntime(section, attempt, store);

    store.on('production:ready', ({ itemId }) => {
      if (isDebugEnabled()) pbLog('production:ready', { itemId });
      const item = store.getState().items.find((row) => row.id === itemId);
      if (!item || !needsHiResGalleryDisplay(item)) return;
      void pipeline.ensureEditorDisplayUrl(itemId).catch((error) => {
        reportBuilderFailure(section, error, {
          stage: 'image-display',
          category: 'secondary-image',
          fatal: false,
          itemId,
        });
      });
    });

    // Upload manager — lazy: module + Filestack SDK; start() also purges orphans
    // after reload (runs even when appKey is missing so ATC cannot stick disabled).
    let uploadManager = null;
    /** @type {Promise<unknown>|null} */
    let uploadManagerPromise = null;
    const resumeUploadsAfterPageShow = (event) => {
      if (!event.persisted) return;
      if (!uploadManager || uploadManager === 'loading') return;
      try {
        uploadManager.resumeAfterPageShow?.();
      } catch (error) {
        pbWarn('upload resume after pageshow failed', error);
      }
    };
    window.addEventListener('pageshow', resumeUploadsAfterPageShow);
    registerSectionCleanup(section, () => {
      window.removeEventListener('pageshow', resumeUploadsAfterPageShow);
      if (uploadManager && uploadManager !== 'loading') uploadManager.destroy?.();
      uploadManager = null;
      uploadManagerPromise = null;
    });
    const appKey = String(payload.filestack?.appKey ?? '').trim();
    section.dataset.pbFilestackConfigured = appKey ? 'true' : '';
    const ensureUploadManager = () => {
      if (uploadManager && uploadManager !== 'loading') {
        return Promise.resolve(uploadManager);
      }
      if (uploadManagerPromise) return uploadManagerPromise;

      uploadManager = 'loading';
      uploadManagerPromise = import('builder-upload')
        .then((mod) => {
          if (!isRuntimeCurrent()) {
            uploadManager = null;
            return null;
          }
          uploadManager = mod.createUploadManager({
            store,
            pipeline,
            appKey,
            log: pbLog,
            warn: pbWarn,
          });
          uploadManager.start();
          return uploadManager;
        })
        .catch((error) => {
          uploadManager = null;
          reportBuilderFailure(section, error, {
            stage: 'upload-manager',
            category: 'secondary-module',
            fatal: false,
          });
          return null;
        });
      return uploadManagerPromise;
    };
    let secondaryRecoveryPromise = null;
    let secondaryRecoveryQueued = false;
    let secondaryRecoveryNeedsCoreSync = false;
    const recoverSecondaryState = ({ syncCore = false } = {}) => {
      if (!isRuntimeCurrent() || section.dataset.pbAppReady !== 'true') return;
      secondaryRecoveryQueued = true;
      secondaryRecoveryNeedsCoreSync ||= syncCore;
      if (secondaryRecoveryPromise) return;

      secondaryRecoveryPromise = (async () => {
        while (secondaryRecoveryQueued && isRuntimeCurrent()) {
          secondaryRecoveryQueued = false;
          const shouldSyncCore = secondaryRecoveryNeedsCoreSync;
          secondaryRecoveryNeedsCoreSync = false;
          if (shouldSyncCore) syncBootUi(section, store);

          const needsUploadManager = store
            .getState()
            .items.some((row) => row.image && !row.image.source?.filestackHandle);
          if (needsUploadManager) void ensureUploadManager();
          await restoreSecondaryImages(section, attempt, store, pipeline);
        }
      })()
        .catch((error) => {
          reportBuilderFailure(section, error, {
            stage: 'secondary-recovery',
            category: 'secondary-image',
            fatal: false,
          });
        })
        .finally(() => {
          secondaryRecoveryPromise = null;
          if (secondaryRecoveryQueued) recoverSecondaryState();
        });
    };
    store.on('production:ready', () => {
      void ensureUploadManager();
    });
    // Start uploads as soon as originals exist — do not wait for local production.
    store.on('item:added', () => {
      void ensureUploadManager();
    });
    store.on('hydrate:late', () => {
      // The soft deadline may have unlocked the UI before a valid saved draft arrived.
      // Once ready, rerun the secondary recovery that normally happens only at boot.
      recoverSecondaryState({ syncCore: true });
    });
    uploadManagerEnsures.set(section, ensureUploadManager);
    registerSectionCleanup(section, () => {
      uploadManagerEnsures.delete(section);
    });

    attempt.stage = 'variant';
    assertVariant(adapter, store.getState().selectedOptions);

    section.dataset.productFamily = payload.family;
    const initialFrame = store.getState().selectedOptions.frame ?? '';
    section.dataset.productFrame = initialFrame ? frameStyleKey(initialFrame) : 'none';

    // Wire the stable shell before persisted core state hydration. Only the
    // mutating regions remain locked until the final core sync is correct.
    attempt.stage = 'shell';
    initShell({ section, payload, adapter, store });
    wireStoreUi(section, store, payload, adapter);
    wireCartAtc(section, store);
    wireSummaryDisclosure(section);
    wireGalleryUpload(section, pipeline);
    wireMoreInfoSheet(section);
    wireCollapsiblePanels(section, store);
    wireMobileSidebarHeight(section);
    syncBootUi(section, store);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollActiveChipsIntoView(section);
      });
    });

    if (isDebugEnabled()) {
      addDebugControls(section, store, adapter);
    }

    attempt.stage = 'hydrate';
    await store.hydrate();
    if (!isCurrentBoot()) return;

    attempt.stage = 'variant';
    assertVariant(adapter, store.getState().selectedOptions);

    attempt.stage = 'core-sync';
    syncBootUi(section, store);
    reportAtcGateAfterBoot(store, pipeline, { appKey });

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollActiveChipsIntoView(section);
      });
    });

    section.dataset.pbAppReady = 'true';
    delete section.dataset.pbBootFailed;
    delete section.dataset.pbBootStage;
    setHydrationLock(section, false);

    requestAnimationFrame(() => {
      recoverSecondaryState();
    });
  } catch (error) {
    if (!isActiveBootAttempt(section, attempt)) return;
    attempt.failed = true;
    setHydrationLock(section, true);
    throw reportBuilderFailure(section, error, {
      stage: attempt.stage,
      category: bootFailureCategory(attempt.stage),
      fatal: true,
    });
  } finally {
    if (bootAttempts.get(section) === attempt) {
      if (!attempt.failed) bootAttempts.delete(section);
      delete section.dataset.pbBooting;
    }
  }
}

function scheduleBoot(section) {
  void bootSection(section).catch((error) => {
    reportBuilderFailure(section, error, {
      stage: 'initialization',
      category: 'programming',
      fatal: true,
    });
  });
}

async function initBuilder() {
  if (!customElements.get('pb-app')) {
    customElements.define(
      'pb-app',
      class PbApp extends HTMLElement {
        connectedCallback() {
          const section = this.closest('[data-pb-section]') ?? this;
          if (section instanceof HTMLElement) scheduleBoot(section);
        }

        disconnectedCallback() {
          const section = this.closest('[data-pb-section]') ?? this;
          if (section instanceof HTMLElement) destroySection(section);
        }
      }
    );
  }

  if (isDebugEnabled()) {
    try {
      runCoordsSelfTest();
      pbLog('coords self-test passed');
    } catch (error) {
      pbWarn('coords self-test failed', error);
    }
  }

  const sections = [...document.querySelectorAll('[data-pb-section]')];
  await Promise.all(sections.map((section) => bootSection(section)));
}

function startBuilder() {
  void initBuilder().catch((error) => {
    const sections = [...document.querySelectorAll('[data-pb-section]')];
    if (!sections.length) {
      reportBuilderFailure(null, error, {
        stage: 'initialization',
        category: 'programming',
        fatal: true,
      });
      return;
    }
    for (const section of sections) {
      if (!(section instanceof HTMLElement)) continue;
      reportBuilderFailure(section, error, {
        stage: 'initialization',
        category: 'programming',
        fatal: true,
      });
    }
  });
}

registerShellEvents((scope) => {
  const root = resolveBuilderSection(scope);
  if (root) scheduleBoot(root);
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', startBuilder, { once: true });
} else {
  startBuilder();
}
